/**
 * Office.js implementation of the pane's hands (spec §5).
 *
 * Written against the structural types in `wordApi.ts`, so it runs unchanged on `Word.run` and on the fake
 * in `fake.ts`. Every read is `load` + `sync` before use; loads are batched so a capture costs two
 * `context.sync()` round trips regardless of document size (plus one for a footnote/header pid on apply).
 *
 * Everything that touches the document goes through one global queue (`documentQueue`).
 */

import { type AnchorLevel, locate, locateInSnapshot, overlapsDeleted, searchProbes, escapeWordSearch } from "../anchor/anchor";
import { type Token, occurrenceCount, occurrenceIndex, tokenize, wordDiff } from "../anchor/diff";
import { codePointLength, normalizeForMatch, replaceLoneSurrogates } from "../anchor/normalize";
import {
  MAX_SNAPSHOT_CHARS,
  type SnapshotComment,
  segmentsFromSpans,
  withoutDeleted,
  type SnapshotParagraph,
  type SnapshotTrackedChange,
  type TrackedChangeType,
  type WordSnapshot,
  documentHash,
  paragraphHash,
} from "../anchor/snapshot";
import { builtInStyle, parseMarkdown } from "./markdown";
import { TaskQueue, documentQueue } from "./queue";
import {
  type AnchorOutcome,
  type AnchorTarget,
  type ApplyOptions,
  type ApplyResult,
  type CaptureOptions,
  type CaptureResult,
  type CommentResult,
  type DocumentHands,
  type InsertRequest,
  type InsertResult,
  type ModeJournal,
  type NavigateResult,
  type PaneComment,
  type PaneEdit,
  type SelectionTarget,
  SnapshotTooLargeError,
  type TrackingMode,
} from "./types";
import type {
  ClientResult,
  ContentControl,
  HeaderFooterType,
  ListItem,
  NoteItem,
  Paragraph,
  ParagraphCollection,
  Range,
  RangeCollection,
  RequestContext,
  SearchOptions,
  StoryBody,
  TableCell,
  TrackedChange,
  TrackedChangeCollection,
  WordApiSupport,
  WordRun,
} from "./wordApi";

export const ELLA_COMMENT = "Suggested by Ella";

const HF_TYPES: readonly HeaderFooterType[] = ["Primary", "FirstPage", "EvenPages"];
/** hf{k}: k = section * 6 + slot; slots 0–2 are headers (Primary, FirstPage, EvenPages), 3–5 footers. */
const HF_SLOTS = 6;

const SEARCH: SearchOptions = {
  matchCase: true,
  matchWholeWord: false,
  matchWildcards: false,
  ignorePunct: false,
  ignoreSpace: false,
};

// ---------------------------------------------------------------------------------------------------------
// pids

type Story = { kind: "body" } | { kind: "fn" | "en"; note: number } | { kind: "hf"; k: number };

interface PidRef {
  story: Story;
  index: number;
}

export function parsePid(pid: string): PidRef | null {
  let m = /^p(\d+)$/.exec(pid);
  if (m) return { story: { kind: "body" }, index: Number(m[1]) };
  m = /^(fn|en)(\d+)\.(\d+)$/.exec(pid);
  if (m) return { story: { kind: m[1] as "fn" | "en", note: Number(m[2]) }, index: Number(m[3]) };
  m = /^hf(\d+)\.(\d+)$/.exec(pid);
  if (m) return { story: { kind: "hf", k: Number(m[1]) }, index: Number(m[2]) };
  return null;
}

function pidFor(story: Story, index: number): string {
  if (story.kind === "body") return `p${index}`;
  if (story.kind === "hf") return `hf${story.k}.${index}`;
  return `${story.kind}${story.note}.${index}`;
}

function hfStory(section: { getHeader(t: HeaderFooterType): StoryBody; getFooter(t: HeaderFooterType): StoryBody }, slot: number): StoryBody {
  return slot < 3 ? section.getHeader(HF_TYPES[slot] as HeaderFooterType) : section.getFooter(HF_TYPES[slot - 3] as HeaderFooterType);
}

function trackedType(raw: string): TrackedChangeType | null {
  if (raw === "Added") return "added";
  if (raw === "Deleted") return "deleted";
  if (raw === "Formatted") return "formatted";
  return null;
}

function isoDate(value: unknown): string | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value === "string" && value) return value;
  return null;
}

/** A mutation failed inside `withTracking`; says whether Track Changes was put back. */
export class TrackingError extends Error {
  constructor(
    readonly original: unknown,
    readonly modeRestoreFailed: boolean,
  ) {
    super(original instanceof Error ? original.message : String(original));
    this.name = "TrackingError";
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function restoreFailed(error: unknown): boolean {
  return error instanceof TrackingError && error.modeRestoreFailed;
}

export const PROTECTED_DETAIL = "Word won't let Ella change this document: it is protected or open read-only.";
export const SELECTION_MOVED_DETAIL =
  "The text Ella rewrote is no longer where it was. Select it again and ask Ella, or insert the draft at a paragraph instead.";
export const UNKNOWN_DELETIONS_DETAIL =
  "This paragraph has pending tracked deletions and Word didn't report exactly where they are, so Ella won't edit it. Accept or reject the tracked changes here first.";
export const SPACING_UNVERIFIED_DETAIL =
  "Word added a space next to this edit, and Ella couldn't confirm it was removed. Check the spacing here.";
export const PLACEMENT_UNVERIFIED_DETAIL =
  "Word placed this edit differently than Ella planned, so Ella couldn't confirm it. Check the tracked changes here.";
export const COMMENT_ON_PARAGRAPH_DETAIL =
  "The comment is on the whole paragraph: Word didn't report where the deleted words are.";
export const CURSOR_MOVED_DETAIL =
  "The paragraph where your cursor was has changed since Ella read the document. Put the cursor where the draft should go and ask Ella again.";

/**
 * Word refuses every change in a protected or read-only document with `AccessDenied` (the message varies by
 * host), so one such error means "stop and say so once", not "this card failed".
 */
export function isProtectionError(error: unknown): boolean {
  const e = error instanceof TrackingError ? error.original : error;
  const code = typeof e === "object" && e !== null ? (e as { code?: unknown }).code : undefined;
  if (code === "AccessDenied") return true;
  return /\bprotected\b|read[- ]only/i.test(errorMessage(e));
}

/** Does `Document.protectionType` forbid this kind of change? (AllowOnlyRevisions allows tracked edits.) */
function protectionBlocks(protection: string | null, what: "edit" | "comment"): boolean {
  if (protection === null || protection === "NoProtection" || protection === "AllowOnlyRevisions") return false;
  if (what === "comment") return protection !== "AllowOnlyComments";
  return true;
}

function isSpaceToken(token: Token): boolean {
  return token.key === " " || !token.text.trim();
}

/**
 * Where to attach text inserted at `offset` of `text` without touching the text around it: a span of real
 * (non-space) characters ending at the offset (insert After it) or starting there (insert Before it). Whole
 * tokens are preferred; a span may end mid-word (a cursor inside a word) or include the space before the
 * offset. Null when there is no non-space character to hold on to.
 */
export function boundarySpan(text: string, offset: number): { start: number; end: number; location: "Before" | "After" } | null {
  const tokens = tokenize(text).filter((t) => !isSpaceToken(t));
  let prev: Token | undefined;
  let next: Token | undefined;
  for (const t of tokens) {
    if (t.start < offset) prev = t;
    if (next === undefined && t.end > offset) next = t;
  }
  if (prev !== undefined && prev.end === offset) return { start: prev.start, end: offset, location: "After" };
  if (next !== undefined && next.start === offset) return { start: offset, end: next.end, location: "Before" };
  if (prev !== undefined) return { start: prev.start, end: offset, location: "After" };
  if (next !== undefined) return { start: offset, end: next.end, location: "Before" };
  return null;
}

/**
 * How to insert `inserted` at `position` of `matched` as ONE insertion next to a real token (spec §5.2 [1.1]):
 * After the non-space token ending there, or Before the one starting there. When the position sits next to a
 * space at the span's edge, the insertion moves across that space (keeping the document's own space and
 * replacing the model's equivalent one). Null: no precise place (the caller replaces the whole span).
 */
export function insertionAnchor(
  matched: string,
  position: number,
  inserted: string,
): { token: Token; location: "Before" | "After"; text: string } | null {
  const tokens = tokenize(matched);
  const i = tokens.findIndex((t) => t.end === position);
  const j = tokens.findIndex((t) => t.start === position);
  const before = i >= 0 ? tokens[i] : undefined;
  const after = j >= 0 ? tokens[j] : undefined;
  if (before !== undefined && !isSpaceToken(before)) return { token: before, location: "After", text: inserted };
  if (after !== undefined && !isSpaceToken(after)) return { token: after, location: "Before", text: inserted };
  const leading = /^\s+/.exec(inserted)?.[0] ?? "";
  const trailing = /\s+$/.exec(inserted)?.[0] ?? "";
  // "W␣|" + "new␣" -> after W: "␣new" (then the document's own space follows).
  const prevWord = before !== undefined && i > 0 ? tokens[i - 1] : undefined;
  if (before !== undefined && prevWord !== undefined && !isSpaceToken(prevWord) && trailing && inserted.trim()) {
    return { token: prevWord, location: "After", text: before.text + inserted.slice(0, inserted.length - trailing.length) };
  }
  // "|␣W" + "␣new" -> before W: "new␣".
  const nextWord = after !== undefined && j + 1 < tokens.length ? tokens[j + 1] : undefined;
  if (after !== undefined && nextWord !== undefined && !isSpaceToken(nextWord) && leading && inserted.trim()) {
    return { token: nextWord, location: "Before", text: inserted.slice(leading.length) + after.text };
  }
  return null;
}

/** `getReviewedText` returns the paragraph WITH its paragraph mark (Word on the web); `Paragraph.text` never has it. */
export function stripParagraphMark(text: string): string {
  return text.replace(/(?:\r\n|\r|\n)$/, "");
}

/** A tracked deletion's text, one piece per paragraph it touches (the first piece is where it starts). */
export function deletionPieces(text: string): string[] {
  return text.split(/\r\n|\r|\n/);
}

/**
 * N1 (spike P9, Word on the web): in a footnote/endnote paragraph every Range that starts at the paragraph's start
 * (`paragraph.getRange('Start').expandTo(...)`, `getRange('Content')`) carries ONE extra leading character, the
 * note's reference mark (reported as U+0020), that `Paragraph.text` lacks; body paragraphs carry none. The count is
 * `Content.text.length − Paragraph.text.length`, trusted only as 0, or as 1 when Content is exactly that one
 * character + `Paragraph.text`. Null: anything else (nothing is placed in that paragraph).
 */
export function leadChars(content: string, text: string): number | null {
  const k = content.length - text.length;
  if (k === 0) return 0;
  if (k === 1 && content.slice(1) === text) return 1;
  return null;
}

/**
 * What Word reports about one pending change, read in one sync (spikes P7, P9, Word on the web):
 * - `text`: `change.getRange().text`. `TrackedChange.text` is shifted by one character in footnotes/endnotes;
 *   the change's own range is not (it does not start at the paragraph start).
 * - `start`: `change.getRange('Start').text`. On the web this is NOT a collapsed point: it holds the whole change.
 * - `prefix`: `firstParagraph.getRange('Start').expandTo(change.getRange('Start')).text`, so on the web it ends
 *   with the change itself, and in a note it starts with the reference mark (N1).
 * - `lead`: the first paragraph's `leadChars` (0 body, 1 note on the web); absent = 0.
 * - `first`: index (in its story) of the paragraph the change starts in.
 */
export interface DeletionRead {
  first: number;
  text: string;
  start: string;
  prefix: string;
  lead?: number;
}

/**
 * Where a pending change sits (used for deletions, and for Ella's own insertions after an edit): `[paragraph
 * index, UTF-16 span]` for each paragraph it touches, or null when Word's own report does not line up exactly.
 * The offset is `prefix.length − lead − start.length` (whether or not Word's start range is collapsed; `lead` =
 * the note reference mark, N1), and it is VERIFIED, never searched for: the prefix before the change (without the
 * mark) must be the start of `Paragraph.text` and the characters at the offset must be exactly the change's text
 * (a deletion across paragraph marks: its first piece runs to the end of its first paragraph, whole middle
 * paragraphs, its last piece starts the last one).
 */
export function placeDeletion(read: DeletionRead, textAt: (index: number) => string | undefined): { index: number; span: [number, number] }[] | null {
  const lead = read.lead ?? 0;
  if (lead !== 0 && lead !== 1) return null;
  // The dropped character is the reference mark, which the web reports as a space (N1); anything else: not placed.
  if (lead === 1 && !STRAY_SPACES.has(read.prefix[0] ?? "")) return null;
  const cut = read.prefix.length - read.start.length;
  const offset = cut - lead;
  if (!read.text || offset < 0 || read.prefix.slice(cut) !== read.start) return null;
  const firstText = textAt(read.first);
  if (firstText === undefined || !firstText.startsWith(read.prefix.slice(lead, cut))) return null;
  const pieces = deletionPieces(read.text);
  const head = pieces[0] as string;
  if (pieces.length === 1) {
    if (!firstText.startsWith(read.prefix.slice(lead)) || firstText.slice(offset, offset + head.length) !== head) return null;
    return [{ index: read.first, span: [offset, offset + head.length] }];
  }
  if (firstText.slice(offset) !== head) return null;
  const placed: { index: number; span: [number, number] }[] = head ? [{ index: read.first, span: [offset, firstText.length] }] : [];
  for (let k = 1; k < pieces.length; k++) {
    const piece = pieces[k] as string;
    const text = textAt(read.first + k);
    if (text === undefined) return null;
    const fits = k < pieces.length - 1 ? text === piece : text.startsWith(piece);
    if (!fits) return null;
    if (piece) placed.push({ index: read.first + k, span: [0, piece.length] });
  }
  return placed;
}

/**
 * Every pending deletion of a story placed by `placeDeletion`. A paragraph is `unknown` when any deletion
 * touching it cannot be placed, or two placed deletions overlap: it then gets no spans at all (its mask is
 * unknown, never partly guessed).
 */
export function placeDeletions(
  reads: readonly (DeletionRead | { first: number; pieces: number })[],
  textAt: (index: number) => string | undefined,
): { spans: Map<number, [number, number][]>; unknown: Set<number> } {
  const spans = new Map<number, [number, number][]>();
  const unknown = new Set<number>();
  for (const read of reads) {
    const placed = "prefix" in read ? placeDeletion(read, textAt) : null;
    if (placed === null) {
      const pieces = "prefix" in read ? deletionPieces(read.text).length : read.pieces;
      for (let k = 0; k < Math.max(1, pieces); k++) unknown.add(read.first + k);
      continue;
    }
    for (const { index, span } of placed) {
      const list = spans.get(index);
      if (list) list.push(span);
      else spans.set(index, [span]);
    }
  }
  for (const [index, list] of spans) {
    list.sort((a, b) => a[0] - b[0]);
    if (list.some((span, i) => i > 0 && span[0] < (list[i - 1] as [number, number])[1])) unknown.add(index);
  }
  for (const index of unknown) spans.delete(index);
  return { spans, unknown };
}

/** Text without Word's comment-anchor marks (U+0005; `getReviewedText` drops some next to deleted text). */
export function withoutCommentMarks(text: string): string {
  return text.replaceAll("\u0005", "");
}

/**
 * Body self-check: `live` minus the placed deletions (insertions stay: they are in both views) must be Word's
 * own accepted view (`getReviewedText('Current')`), comment marks and the paragraph mark aside.
 */
export function reviewedAgrees(live: string, deleted: readonly (readonly [number, number])[], reviewed: string): boolean {
  const segments = segmentsFromSpans(live, deleted);
  const accepted = segments === null ? live : withoutDeleted(segments);
  return withoutCommentMarks(accepted) === withoutCommentMarks(stripParagraphMark(reviewed));
}

/** Spaces Word adds next to an edit; the web reports a no-break space as U+202F in `Paragraph.text` (P7). */
const STRAY_SPACES: ReadonlySet<string> = new Set([" ", "\u00a0", "\u202f"]);

/**
 * Word's "smart cut" for a delete-only edit whose quote leaves out the spaces around it: the span
 * `[start, end)` of `text` (live text; `deleted` = its placed pending deletions, sorted) grows by ONE space so the
 * accepted text does not end up with a double space ("store X a" minus "X" -> "store a", not "store  a").
 *
 * Judged on the accepted view (live text minus `deleted`): when the characters just before and just after the
 * span there are both spaces, the space right after the span is taken, else the one right before it. At the
 * paragraph's start (nothing before in the accepted view) only a following space is taken; at its end only a
 * preceding one. The space taken must sit right next to the span in the live text and outside every pending
 * deletion (never across a tracked change boundary). Otherwise the span is returned unchanged.
 */
export function smartDeleteSpan(text: string, start: number, end: number, deleted: readonly (readonly [number, number])[]): [number, number] {
  if (start >= end || !text.slice(start, end).trim()) return [start, end];
  const isDeleted = (i: number): boolean => deleted.some(([s, e]) => s <= i && i < e);
  const isSpace = (i: number): boolean => i >= 0 && i < text.length && STRAY_SPACES.has(text[i] as string);
  let prev = start - 1;
  while (prev >= 0 && isDeleted(prev)) prev--;
  let next = end;
  while (next < text.length && isDeleted(next)) next++;
  const hasPrev = prev >= 0;
  const hasNext = next < text.length;
  const trailing = isSpace(end) && !isDeleted(end);
  const leading = isSpace(start - 1) && !isDeleted(start - 1);
  if (hasPrev && hasNext) {
    if (!isSpace(prev) || !isSpace(next)) return [start, end];
    if (trailing) return [start, end + 1];
    if (leading) return [start - 1, end];
    return [start, end];
  }
  if (!hasPrev && hasNext && isSpace(next) && trailing) return [start, end + 1];
  if (hasPrev && !hasNext && isSpace(prev) && leading) return [start - 1, end];
  return [start, end];
}
const MAX_STRAYS = 4;
const MAX_STRAY_STATES = 2_000_000;

/**
 * Spaces Word itself added next to an edit (spike P2 B1: in a footnote on the web, a tracked replace of
 * "terms" by "provisions" also inserted a no-break space, leaving "Capitalized  provisions" once accepted).
 *
 * `before` is the paragraph text the edit was planned on, `[start, end)` the anchored span (edits only touch
 * that span) and `after` the paragraph text Word reports afterwards. Word on the web keeps tracked-deleted
 * text in `Paragraph.text`, so the new span must be an interleaving of the old span, `inserted` (what Ella
 * meant to add, in order) and a few spaces/no-break spaces. Those extra characters are the strays: returns
 * their UTF-16 offsets in `after` (empty: none). Among several readings the old text is taken as early as
 * possible, so a stray is the later of two equal spaces (Word adds it next to its insertion). Null when the
 * text is not such an interleaving (e.g. a host that drops deleted text) — then nothing is touched.
 */
export function strayWhitespace(before: string, start: number, end: number, after: string, inserted: string): number[] | null {
  // The web shows a no-break space as U+202F (F6); Ella writes U+00A0. Same character here (length-preserving).
  before = sameSpaces(before);
  after = sameSpaces(after);
  inserted = sameSpaces(inserted);
  const tail = before.length - end;
  if (after.length < start + tail || after.slice(0, start) !== before.slice(0, start) || after.slice(after.length - tail) !== before.slice(end)) {
    return null;
  }
  const oldSpan = before.slice(start, end);
  const newSpan = after.slice(start, after.length - tail);
  const n = oldSpan.length;
  const q = inserted.length;
  const strays = newSpan.length - n - q;
  if (strays < 0 || strays > MAX_STRAYS) return null;
  if ((n + 1) * (q + 1) * (strays + 1) > MAX_STRAY_STATES) return null;
  // done[i][j][k]: from i old, j inserted and k stray characters consumed, the rest of newSpan can be read.
  const W = q + 1;
  const K = strays + 1;
  const at = (i: number, j: number, k: number): number => (i * W + j) * K + k;
  const done = new Uint8Array((n + 1) * W * K);
  for (let i = n; i >= 0; i--) {
    for (let j = q; j >= 0; j--) {
      for (let k = strays; k >= 0; k--) {
        const y = i + j + k;
        if (y === newSpan.length) {
          done[at(i, j, k)] = i === n && j === q && k === strays ? 1 : 0;
          continue;
        }
        const ch = newSpan[y] as string;
        done[at(i, j, k)] =
          (i < n && oldSpan[i] === ch && done[at(i + 1, j, k)] === 1) ||
          (j < q && inserted[j] === ch && done[at(i, j + 1, k)] === 1) ||
          (k < strays && STRAY_SPACES.has(ch) && done[at(i, j, k + 1)] === 1)
            ? 1
            : 0;
      }
    }
  }
  if (done[at(0, 0, 0)] !== 1) return null;
  const found: number[] = [];
  let i = 0;
  let j = 0;
  let k = 0;
  while (i + j + k < newSpan.length) {
    const y = i + j + k;
    const ch = newSpan[y] as string;
    if (i < n && oldSpan[i] === ch && done[at(i + 1, j, k)] === 1) i++;
    else if (j < q && inserted[j] === ch && done[at(i, j + 1, k)] === 1) j++;
    else {
      found.push(start + y);
      k++;
    }
  }
  return found;
}

/** Only spaces / no-break spaces (Word's stray insertions). */
export function isStraySpaceText(text: string): boolean {
  return text.length > 0 && [...text].every((ch) => STRAY_SPACES.has(ch));
}

/** U+202F (how the web reports a no-break space, F6) read as U+00A0. Length-preserving. */
export function sameSpaces(text: string): string {
  return text.replaceAll("\u202f", "\u00a0");
}

/**
 * Text Ella writes into the document: a U+202F the model copied from `Paragraph.text` is the file's U+00A0
 * (F6), so it is written back as U+00A0, never as U+202F.
 */
export function forFile(text: string): string {
  return sameSpaces(text);
}

/** Word cannot search for its comment-anchor mark (U+0005); spans are found around it. */
const UNSEARCHABLE = "\u0005";

function clipQuote(quote: string, limit = 60): string {
  const flat = quote.replace(/\s+/g, " ").trim();
  return flat.length <= limit ? flat : `${flat.slice(0, limit - 1).trimEnd()}…`;
}

function asTrackingMode(mode: string): TrackingMode {
  return mode === "TrackAll" || mode === "TrackMineOnly" ? mode : "Off";
}

function randomUuid(): string {
  return crypto.randomUUID();
}

/** Same story as `pid` (body, one footnote/endnote, one header/footer). */
function storyKey(pid: string): string {
  const ref = parsePid(pid);
  if (ref === null) return pid;
  const story = ref.story;
  return story.kind === "body" ? "body" : story.kind === "hf" ? `hf${story.k}` : `${story.kind}${story.note}`;
}

/** The capture's selection/cursor as a target that survives the user moving the selection afterwards. */
function selectionTarget(
  paragraphs: readonly SnapshotParagraph[],
  pidStart: string,
  pidEnd: string,
  rawText: string,
  place: { prefix: string; start: string; lead: number | null } | null,
): SelectionTarget | null {
  const byPid = new Map(paragraphs.map((p) => [p.pid, p]));
  const text = rawText.replace(/[\r\n]+$/, ""); // a selected paragraph mark is not part of what Ella rewrote
  const multi = /[\r\n]/.test(text);
  const first = byPid.get(pidStart);
  const last = byPid.get(multi ? pidEnd : pidStart);
  if (first === undefined || last === undefined) return null;
  // `place.prefix` runs from the paragraph start through `selection.getRange('Start')` (`place.start`), which
  // need not be a collapsed point (it is not for tracked changes on the web, spike P7): the offset is where the
  // start range begins (less a note's reference mark, N1), and it is only trusted when the prefix before it is the
  // start of the paragraph text.
  let startOffset: number | null = null;
  if (place !== null && place.lead !== null) {
    const cut = place.prefix.length - place.start.length;
    const offset = cut - place.lead;
    if (offset >= 0 && place.prefix.slice(cut) === place.start && first.text_live.startsWith(place.prefix.slice(place.lead, cut))) startOffset = offset;
  }
  let endOffset: number | null = null;
  if (!multi) {
    if (startOffset !== null && first.text_live.slice(startOffset, startOffset + text.length) === text) endOffset = startOffset + text.length;
    else startOffset = null; // Word's range text did not line up with the paragraph text: trust no offset
  } else {
    const lastPart = text.slice(Math.max(text.lastIndexOf("\r"), text.lastIndexOf("\n")) + 1);
    endOffset = last.text_live.startsWith(lastPart) ? lastPart.length : null;
  }
  const key = storyKey(first.pid);
  return {
    start: { pid: first.pid, paragraphHash: first.hash, offset: startOffset },
    end: { pid: last.pid, paragraphHash: last.hash, offset: endOffset },
    text,
    storyParagraphs: paragraphs.filter((p) => storyKey(p.pid) === key).length,
  };
}

// ---------------------------------------------------------------------------------------------------------
// hands

export interface WordHandsOptions {
  run: WordRun;
  /** `Office.context.requirements.isSetSupported('WordApi', version)`. */
  supports: WordApiSupport;
  /** `isSetSupported('WordApiDesktop', version)` (document protection); absent = none. */
  supportsDesktop?: WordApiSupport;
  queue?: TaskQueue;
  now?: () => number;
  newId?: () => string;
  /**
   * Put comments about footnote/endnote text on the note's reference mark in the main text (Word on the web:
   * a comment inside a note is lost from the exported/saved file, leaving a dangling anchor; spike P2 B3).
   */
  noteCommentsOnReference?: boolean;
  /**
   * Word on the web: `Paragraph.text` keeps tracked-deleted words, so after an edit the paragraph text must read
   * as the old text interleaved with Ella's (`strayWhitespace`); when it does not, spacing is unverified.
   */
  webHost?: boolean;
}

interface Located {
  pid: string;
  resolution: "pid" | "hash" | "search";
  paragraph: Paragraph;
  text: string;
  start: number;
  end: number;
  level: AnchorLevel;
  /** The story's paragraphs (reloaded after an edit), the paragraph's index there, and the story's size. */
  collection: ParagraphCollection;
  index: number;
  storyLength: number;
  /** The paragraph's pending changes, when already read (an ambiguous quote needs its deletions). */
  state?: ParagraphState;
}

/** One pending change as a before/after diff key: type, author and its range's text. */
interface ChangeSig {
  type: string;
  author: string;
  text: string;
}

/**
 * A paragraph's pending changes before an edit. `deleted`: UTF-16 spans of `Paragraph.text` that pending
 * deletions hold, `[]` when none, null when unknown (a deletion could not be placed exactly: edits there are
 * refused). `before`: every change, for the after-edit diff (null: could not be read).
 */
interface ParagraphState {
  deleted: [number, number][] | null;
  before: ChangeSig[] | null;
}

const UNKNOWN_STATE: ParagraphState = { deleted: null, before: null };

const sigKey = (sig: ChangeSig): string => `${sig.type}\u0000${sig.author}\u0000${sig.text}`;

type LocateFailure = Pick<AnchorOutcome, "status" | "detail"> & { pid: string | null };

export function createWordHands(options: WordHandsOptions): DocumentHands {
  const { run, supports } = options;
  const supportsDesktop = options.supportsDesktop ?? ((): boolean => false);
  const queue = options.queue ?? documentQueue;
  const now = options.now ?? (() => performance.now());
  const newId = options.newId ?? randomUuid;
  const noteCommentsOnReference = options.noteCommentsOnReference ?? false;
  const webHost = options.webHost ?? false;

  // ---- locating -----------------------------------------------------------------------------------------

  async function storyParagraphs(ctx: RequestContext, story: Story): Promise<ParagraphCollection | null> {
    const body = ctx.document.body;
    if (story.kind === "body") return body.paragraphs;
    if (story.kind === "fn" || story.kind === "en") {
      if (!supports("1.5")) return null;
      const notes = story.kind === "fn" ? body.footnotes : body.endnotes;
      notes.load("items");
      await ctx.sync();
      const note = notes.items[story.note - 1];
      return note ? note.body.paragraphs : null;
    }
    const k = story.kind === "hf" ? story.k : -1;
    const sections = ctx.document.sections;
    sections.load("items");
    await ctx.sync();
    const section = sections.items[Math.floor(k / HF_SLOTS)];
    return section ? hfStory(section, k % HF_SLOTS).paragraphs : null;
  }

  /** Find the paragraph (pid + hash, unique hash, unique ladder search in the story), then the quote in it. */
  async function locateParagraphAndSpan(
    ctx: RequestContext,
    target: AnchorTarget,
    forEdit: boolean,
  ): Promise<{ ok: true; located: Located } | { ok: false; failure: LocateFailure }> {
    const ref = parsePid(target.pid);
    if (ref === null) return { ok: false, failure: { status: "not_found", pid: null, detail: `There is no paragraph ${target.pid}.` } };
    const collection = await storyParagraphs(ctx, ref.story);
    if (collection === null) {
      return { ok: false, failure: { status: "not_found", pid: null, detail: `Paragraph ${target.pid} is not in this document any more.` } };
    }
    collection.load("items/text");
    await ctx.sync();
    const items = collection.items;
    const texts = items.map((p) => replaceLoneSurrogates(p.text));

    let chosen: number | null = null;
    let resolution: Located["resolution"] = "pid";
    const atPid = texts[ref.index];
    if (atPid !== undefined && (target.paragraphHash === undefined || (await paragraphHash(atPid)) === target.paragraphHash)) {
      chosen = ref.index;
    } else if (target.paragraphHash !== undefined) {
      const hashes = await Promise.all(texts.map((t) => paragraphHash(t)));
      const matches = hashes.flatMap((h, i) => (h === target.paragraphHash ? [i] : []));
      if (matches.length === 1) {
        chosen = matches[0] as number;
        resolution = "hash";
      }
    }

    if (chosen !== null) {
      const text = texts[chosen] as string;
      const pid = pidFor(ref.story, chosen);
      let result = locate(text, target.quote);
      let state: ParagraphState | undefined;
      if (result.status === "ambiguous" && supports("1.6")) {
        // Word on the web keeps tracked-deleted text in Paragraph.text: one occurrence may be deleted words.
        state = await readParagraphState(ctx, items[chosen] as Paragraph, text, pid);
        if (state.deleted !== null && state.deleted.length > 0) result = locate(text, target.quote, state.deleted);
        if (result.status === "ambiguous" && state.deleted === null && forEdit) {
          return { ok: false, failure: { status: "pending_change", pid, detail: UNKNOWN_DELETIONS_DETAIL } };
        }
      }
      if (result.status === "ambiguous") {
        return { ok: false, failure: { status: "ambiguous", pid, detail: `The quoted text appears ${result.matchCount} times in this paragraph.` } };
      }
      if (result.status === "not_found" || result.start === null || result.end === null || result.level === null) {
        return { ok: false, failure: { status: "not_found", pid, detail: "The document changed here; the quoted text is no longer in this paragraph." } };
      }
      return {
        ok: true,
        located: {
          pid,
          resolution,
          paragraph: items[chosen] as Paragraph,
          text,
          start: result.start,
          end: result.end,
          level: result.level,
          collection,
          index: chosen,
          storyLength: items.length,
          ...(state !== undefined ? { state } : {}),
        },
      };
    }

    // The paragraph moved or changed: the quote must be unique across the whole story.
    const pseudo: SnapshotParagraph[] = texts.map((t, i) => ({ pid: pidFor(ref.story, i), text_live: t, text_reviewed: t, hash: "" }));
    const found = locateInSnapshot({ paragraphs: pseudo }, "\u0000", target.quote);
    if (found.result.status === "ambiguous") {
      return { ok: false, failure: { status: "ambiguous", pid: null, detail: "The quoted text now appears in more than one place." } };
    }
    if (found.result.status !== "ok" || found.pid === null || found.result.start === null || found.result.end === null || found.result.level === null) {
      return { ok: false, failure: { status: "not_found", pid: null, detail: "The document changed here; the quoted text was not found." } };
    }
    const index = pseudo.findIndex((p) => p.pid === found.pid);
    return {
      ok: true,
      located: {
        pid: found.pid,
        resolution: "search",
        paragraph: items[index] as Paragraph,
        text: texts[index] as string,
        start: found.result.start,
        end: found.result.end,
        level: found.result.level,
        collection,
        index,
        storyLength: items.length,
      },
    };
  }

  /**
   * The pending changes of `paragraph` (pid `pid`, its text as located: `text`): where its deletions sit
   * (`placeDeletion`: verified from Word's own ranges, never searched; spike P7) and the list for the after-edit
   * diff. Only this paragraph and the paragraphs its deletions touch are read (never the whole story); the
   * paragraph is identified by `uniqueLocalId` and must still hold `text`. A note's prefixes carry its reference
   * mark (N1: `leadChars` from the first touched paragraph's Content range, read in the same sync). Body
   * paragraphs are also checked against Word's accepted view: the placement must reproduce `getReviewedText`, and
   * when it cannot be placed but `Paragraph.text` already IS the accepted view (a host that leaves deleted text
   * out), nothing is masked. Notes never use `getReviewedText` (N2: shifted by one character on the web).
   * Anything else: `deleted` null (unknown). One sync (plus one when `loaded` is not given).
   */
  async function readParagraphState(
    ctx: RequestContext,
    paragraph: Paragraph,
    text: string,
    pid: string,
    loaded?: TrackedChangeCollection,
  ): Promise<ParagraphState> {
    try {
      let changes = loaded;
      if (changes === undefined) {
        changes = paragraph.getTrackedChanges();
        changes.load("items/type,items/author");
        await ctx.sync();
      }
      const items = changes.items;
      if (items.length === 0) return { deleted: [], before: [] };
      const reads = items.map((change) => {
        const whole = change.getRange();
        whole.load("text");
        if (change.type !== "Deleted") return { change, whole, touched: null, start: null, prefix: null, content: null };
        const touched = whole.paragraphs;
        touched.load("items/text,items/uniqueLocalId");
        const start = change.getRange("Start");
        start.load("text");
        const firstTouched = touched.getFirst();
        const prefix = firstTouched.getRange("Start").expandTo(start);
        prefix.load("text");
        // N1: a note's ranges from the paragraph start carry its reference mark; Content says how many characters.
        const content = firstTouched.getRange("Content");
        content.load("text");
        return { change, whole, touched, start, prefix, content };
      });
      const anyDeleted = reads.some((r) => r.touched !== null);
      if (anyDeleted) paragraph.load("text,uniqueLocalId");
      const body = /^p\d+$/.test(pid);
      const reviewed = anyDeleted && body && supports("1.4") ? paragraph.getReviewedText("Current") : null;
      await ctx.sync();
      const before = reads.map((r) => ({
        type: r.change.type,
        author: replaceLoneSurrogates(r.change.author ?? ""),
        text: replaceLoneSurrogates(r.whole.text),
      }));
      if (!anyDeleted) return { deleted: [], before };
      const live = replaceLoneSurrogates(paragraph.text);
      if (live !== text) return { deleted: null, before };
      const uid = paragraph.uniqueLocalId;
      const deleted: [number, number][] = [];
      let unknown = false;
      for (const r of reads) {
        if (r.touched === null || r.start === null || r.prefix === null || r.content === null) continue;
        const local = r.touched.items.map((p) => replaceLoneSurrogates(p.text));
        const here = r.touched.items.findIndex((p) => p.uniqueLocalId === uid);
        const lead = local[0] === undefined ? null : leadChars(replaceLoneSurrogates(r.content.text), local[0]);
        const read: DeletionRead = {
          first: 0,
          text: replaceLoneSurrogates(r.whole.text),
          start: replaceLoneSurrogates(r.start.text),
          prefix: replaceLoneSurrogates(r.prefix.text),
          lead: lead ?? 0,
        };
        const placed = here < 0 || lead === null ? null : placeDeletion(read, (i) => local[i]);
        if (placed === null) {
          unknown = true;
          continue;
        }
        for (const { index, span } of placed) if (index === here) deleted.push(span);
      }
      deleted.sort((x, y) => x[0] - y[0]);
      if (deleted.some((span, i) => i > 0 && span[0] < (deleted[i - 1] as [number, number])[1])) unknown = true;
      const rv = reviewed === null ? null : replaceLoneSurrogates(reviewed.value);
      if (unknown) return { deleted: rv !== null && reviewedAgrees(live, [], rv) ? [] : null, before };
      if (rv !== null && !reviewedAgrees(live, deleted, rv)) return { deleted: null, before };
      return { deleted, before };
    } catch {
      return UNKNOWN_STATE;
    }
  }

  /**
   * Of `changes` (the paragraph's list after the edit, items loaded), those the edit added (diff against `before`,
   * by type, author and range text) that are only spaces: the stray no-break space Word on the web adds next to a
   * tracked replace in a note (spike P7, F4). Ella's own whitespace-only insertions (`ellaTexts`; U+00A0 and
   * U+202F count as the same character) are not strays. One sync. Null: cannot tell (a stray looks exactly like
   * a change that was already there).
   */
  async function newStraySpaces(
    ctx: RequestContext,
    changes: TrackedChangeCollection,
    before: readonly ChangeSig[],
    ellaTexts: readonly string[],
  ): Promise<TrackedChange[] | null> {
    const wholes = changes.items.map((c) => {
      const whole = c.getRange();
      whole.load("text");
      return whole;
    });
    if (wholes.length > 0) await ctx.sync();
    const had = new Map<string, number>();
    for (const sig of before) had.set(sigKey(sig), (had.get(sigKey(sig)) ?? 0) + 1);
    const ella = ellaTexts.filter((t) => isStraySpaceText(t)).map(sameSpaces);
    const strays: TrackedChange[] = [];
    let ambiguous = false;
    changes.items.forEach((change, i) => {
      const sig = { type: change.type, author: replaceLoneSurrogates(change.author ?? ""), text: replaceLoneSurrogates((wholes[i] as Range).text) };
      const key = sigKey(sig);
      const left = had.get(key) ?? 0;
      if (left > 0) {
        had.set(key, left - 1);
        if (change.type === "Added" && isStraySpaceText(sig.text)) ambiguous = true; // which one is new?
        return;
      }
      if (change.type !== "Added" || !isStraySpaceText(sig.text)) return;
      const mine = ella.indexOf(sameSpaces(sig.text));
      if (mine >= 0) ella.splice(mine, 1);
      else strays.push(change);
    });
    return ambiguous && strays.length > 0 ? null : strays;
  }

  /**
   * The Deleted change a delete-only edit just made, as a range to comment on (spike P7, F5: after a tracked
   * `range.delete()` on the web the range is empty, while the new change's range is exactly the deleted words).
   * Identified by POSITION, never by list order or text alone: its offset (`prefix − lead − start`, F1/N1, verified
   * like `placeDeletion`) must be exactly the anchored span `[start, start + matched.length)` of `after` (the
   * paragraph's text now; the web keeps deleted words in place) and its text exactly `matched`. The anchored span
   * held no pending deletion (else the edit was refused), so such a change is the new one. Null unless exactly one.
   */
  async function newDeletionRange(ctx: RequestContext, paragraph: Paragraph, after: string, start: number, matched: string): Promise<Range | null> {
    if (after.slice(start, start + matched.length) !== matched) return null;
    const changes = paragraph.getTrackedChanges();
    changes.load("items/type");
    await ctx.sync();
    const reads = changes.items
      .filter((c) => c.type === "Deleted")
      .map((change) => {
        const whole = change.getRange();
        whole.load("text");
        const startRange = change.getRange("Start");
        startRange.load("text");
        const prefix = paragraph.getRange("Start").expandTo(startRange);
        prefix.load("text");
        return { whole, startRange, prefix };
      });
    if (reads.length === 0) return null;
    const content = paragraph.getRange("Content");
    content.load("text");
    await ctx.sync();
    const lead = leadChars(replaceLoneSurrogates(content.text), after);
    if (lead === null) return null;
    const found = reads.filter(({ whole, startRange, prefix }) => {
      const read: DeletionRead = {
        first: 0,
        text: replaceLoneSurrogates(whole.text),
        start: replaceLoneSurrogates(startRange.text),
        prefix: replaceLoneSurrogates(prefix.text),
        lead,
      };
      if (read.text !== matched) return false;
      const placed = placeDeletion(read, (i) => (i === 0 ? after : undefined));
      return placed !== null && placed.length === 1 && placed[0]!.span[0] === start && placed[0]!.span[1] === start + matched.length;
    });
    return found.length === 1 ? found[0]!.whole : null;
  }

  /** Word range for `text.slice(start, end)` inside `paragraph`, via search probes (+ expandTo for long spans). */
  async function rangeForSpan(ctx: RequestContext, paragraph: Paragraph, text: string, start: number, end: number): Promise<Range | null> {
    const matched = text.slice(start, end);
    if (matched.includes(UNSEARCHABLE)) {
      // Word's comment-anchor mark cannot be searched for: find the text before the first mark and after the
      // last one, and join them (the joined text is compared below like any long span).
      const first = matched.indexOf(UNSEARCHABLE);
      const last = matched.lastIndexOf(UNSEARCHABLE);
      const headEnd = start + first;
      const tailStart = start + last + 1;
      if (!matched.slice(0, first).trim() || !matched.slice(last + 1).trim()) return null;
      const head = await rangeForSpan(ctx, paragraph, text, start, headEnd);
      const tail = head === null ? null : await rangeForSpan(ctx, paragraph, text, tailStart, end);
      if (head === null || tail === null) return null;
      const joined = head.expandTo(tail);
      joined.load("text");
      await ctx.sync();
      const live = normalizeForMatch(replaceLoneSurrogates(joined.text)).text;
      return live === normalizeForMatch(matched).text ? joined : null;
    }
    const probes = searchProbes(matched);
    const heads = paragraph.search(probes.head, SEARCH);
    heads.load("items/text");
    const tails = probes.tail !== null ? paragraph.search(probes.tail, SEARCH) : null;
    tails?.load("items/text");
    await ctx.sync();
    const pick = (results: Range[], raw: string, position: number): Range | null => {
      const k = occurrenceIndex(text, raw, position);
      // Word's search must see exactly the occurrences we see, or we cannot tell which one is ours.
      if (k < 0 || results.length !== occurrenceCount(text, raw)) return null;
      return results[k] ?? null;
    };
    const head = pick(heads.items, probes.headRaw, start);
    if (head === null) return null;
    let range = head;
    if (tails !== null && probes.tailRaw !== null) {
      const tail = pick(tails.items, probes.tailRaw, end - probes.tailRaw.length);
      if (tail === null) return null;
      range = head.expandTo(tail);
    }
    range.load("text");
    await ctx.sync();
    const live = normalizeForMatch(replaceLoneSurrogates(range.text)).text;
    return live === normalizeForMatch(matched).text ? range : null;
  }

  interface Anchored {
    located: Located;
    range: Range;
    mode: string;
    /** `Document.protectionType`, or null when this Word cannot report it. */
    protection: string | null;
    /** The paragraph's pending changes before the edit (edits only, WordApi 1.6); null otherwise. */
    state: ParagraphState | null;
  }

  const canReadProtection = (): boolean => supportsDesktop("1.4");

  /** Locate + range + lock/pending checks (and the current tracking mode, in the same sync). */
  async function anchorRange(
    ctx: RequestContext,
    target: AnchorTarget,
    checkPending: boolean,
  ): Promise<{ ok: true; anchored: Anchored } | { ok: false; failure: LocateFailure; level?: AnchorLevel; resolution?: Located["resolution"] }> {
    const found = await locateParagraphAndSpan(ctx, target, checkPending);
    if (!found.ok) return found;
    const located = found.located;
    const range = await rangeForSpan(ctx, located.paragraph, located.text, located.start, located.end);
    if (range === null) {
      return {
        ok: false,
        failure: { status: "not_found", pid: located.pid, detail: "Word could not find this exact text; the document may have changed here." },
      };
    }
    const control: ContentControl = located.paragraph.parentContentControlOrNullObject;
    control.load("cannotEdit");
    let changes: TrackedChangeCollection | null = null;
    let paragraphChanges: TrackedChangeCollection | null = null;
    if (checkPending && supports("1.6")) {
      changes = range.getTrackedChanges();
      changes.load("items/type");
      if (located.state === undefined) {
        paragraphChanges = located.paragraph.getTrackedChanges();
        paragraphChanges.load("items/type,items/author");
      }
    }
    const doc = ctx.document;
    if (supports("1.4")) doc.load("changeTrackingMode");
    if (canReadProtection()) doc.load("protectionType");
    await ctx.sync();
    if (!control.isNullObject && control.cannotEdit) {
      return { ok: false, failure: { status: "locked", pid: located.pid, detail: "This text is in a locked content control; Word will not change it." } };
    }
    if (changes !== null && changes.items.some((c) => c.type === "Added" || c.type === "Deleted")) {
      return {
        ok: false,
        failure: { status: "pending_change", pid: located.pid, detail: "This text has a pending tracked change. Accept or reject it first." },
      };
    }
    let state: ParagraphState | null = null;
    if (changes !== null) {
      // Word on the web keeps deleted words in Paragraph.text: where they sit must be known exactly, or nothing
      // in this paragraph is edited (never guessed).
      state =
        located.state ??
        (await readParagraphState(ctx, located.paragraph, located.text, located.pid, paragraphChanges ?? undefined));
      if (state.deleted === null) {
        return { ok: false, failure: { status: "pending_change", pid: located.pid, detail: UNKNOWN_DELETIONS_DETAIL } };
      }
      if (overlapsDeleted(located.start, located.end, state.deleted)) {
        return {
          ok: false,
          failure: { status: "pending_change", pid: located.pid, detail: "This text has a pending tracked change. Accept or reject it first." },
        };
      }
    }
    return {
      ok: true,
      anchored: {
        located,
        range,
        mode: supports("1.4") ? doc.changeTrackingMode : "Off",
        protection: canReadProtection() ? (doc.protectionType ?? null) : null,
        state,
      },
    };
  }

  // ---- tracking mode around mutations ---------------------------------------------------------------------

  /**
   * Run `mutate` with Track Changes set for `mode`, restoring the user's mode afterwards whether or not
   * `mutate` throws. Returns `refused` when a clean edit is asked for while the document tracks changes.
   * A failure inside `mutate` is rethrown as `TrackingError`, which says whether the restore worked.
   */
  async function withTracking<T>(
    ctx: RequestContext,
    currentMode: string,
    mode: "tracked" | "clean",
    mutate: () => Promise<T>,
    journal?: ModeJournal,
  ): Promise<{ refused: true } | { refused: false; value: T; modeRestoreFailed: boolean }> {
    if (mode === "clean" && currentMode !== "Off") return { refused: true };
    if (mode === "tracked" && !supports("1.4")) throw new Error("Track Changes needs Word API 1.4.");
    const doc = ctx.document;
    const flip = mode === "tracked" && currentMode !== "TrackAll";
    let flipped = false;
    let result: { ok: true; value: T } | { ok: false; error: unknown };
    try {
      if (flip) {
        // Written down before the flip: if the pane dies before the restore, the next load offers to undo it.
        journal?.begin(asTrackingMode(currentMode));
        flipped = true; // set before the sync: if the sync fails midway we still try to put it back
        doc.changeTrackingMode = "TrackAll";
        await ctx.sync();
      }
      result = { ok: true, value: await mutate() };
    } catch (error) {
      result = { ok: false, error };
    }
    let modeRestoreFailed = false;
    if (flipped) {
      try {
        doc.changeTrackingMode = currentMode;
        await ctx.sync();
      } catch {
        // The restore failed; it only matters if the flip happened (a protected document refuses both).
        try {
          doc.load("changeTrackingMode");
          await ctx.sync();
          modeRestoreFailed = doc.changeTrackingMode !== currentMode;
        } catch {
          modeRestoreFailed = true;
        }
      }
      if (!modeRestoreFailed) journal?.end();
    }
    if (!result.ok) throw new TrackingError(result.error, modeRestoreFailed);
    return { refused: false, value: result.value, modeRestoreFailed };
  }

  // ---- capture ----------------------------------------------------------------------------------------------

  async function capture(ctx: RequestContext, captureOptions: CaptureOptions): Promise<CaptureResult> {
    const started = now();
    const has14 = supports("1.4");
    const has15 = supports("1.5");
    const has16 = supports("1.6");
    let syncs = 0;
    const sync = async (): Promise<void> => {
      syncs++;
      await ctx.sync();
    };
    const doc = ctx.document;
    const body = doc.body;
    const paraProps = ["text", "style", "isListItem", "tableNestingLevel", ...(has16 ? ["uniqueLocalId"] : [])];
    const storyProps = ["text", "style", ...(has16 ? ["uniqueLocalId"] : [])];
    const itemsOf = (props: string[]): string => props.map((p) => `items/${p}`).join(",");

    // ---- sync 1: paragraphs, selection, tracked changes, comments, notes, sections --------------------------
    body.paragraphs.load(itemsOf(paraProps));
    doc.properties.load("title");
    const selection = doc.getSelection();
    selection.load("text,isEmpty");
    const selFirst = selection.paragraphs.getFirst();
    const selLast = selection.paragraphs.getLast();
    const selProps = has16 ? "uniqueLocalId,text" : "text";
    selFirst.load(selProps);
    selLast.load(selProps);
    body.contentControls.load("items/cannotEdit");
    const tracked = has16 ? body.getTrackedChanges() : null;
    tracked?.load("items/type,items/author,items/date,items/text");
    const comments = has14 ? body.getComments() : null;
    comments?.load("items/id,items/authorName,items/content,items/resolved");
    const footnotes = has15 ? body.footnotes : null;
    const endnotes = has15 ? body.endnotes : null;
    footnotes?.load("items");
    endnotes?.load("items");
    doc.sections.load("items");
    await sync();

    // Loaded in sync 2 (body tracked changes do not include footnote/endnote ones; spike P2).
    const noteTracked: { story: Story; changes: TrackedChangeCollection }[] = [];

    const bodyItems = body.paragraphs.items;
    let bodyChars = 0;
    for (const p of bodyItems) bodyChars += codePointLength(p.text);
    if (bodyChars > MAX_SNAPSHOT_CHARS) throw new SnapshotTooLargeError(bodyChars);

    // ---- sync 2: per-paragraph extras, change/comment positions, note and header/footer paragraphs -------
    const anyLockable = body.contentControls.items.some((c) => c.cannotEdit);
    const hasTracked = tracked !== null && tracked.items.length > 0;
    const wantReviewed = has14 && (hasTracked || !has16);
    const lists: (ListItem | null)[] = [];
    const cells: (TableCell | null)[] = [];
    const controls: (ContentControl | null)[] = [];
    const reviewed: (ClientResult<string> | null)[] = [];
    for (const p of bodyItems) {
      let list: ListItem | null = null;
      if (p.isListItem) {
        list = p.listItemOrNullObject;
        list.load("listString");
      }
      lists.push(list);
      let cell: TableCell | null = null;
      if (p.tableNestingLevel > 0) {
        cell = p.parentTableCellOrNullObject;
        cell.load("rowIndex,cellIndex");
      }
      cells.push(cell);
      let control: ContentControl | null = null;
      if (anyLockable) {
        control = p.parentContentControlOrNullObject;
        control.load("cannotEdit");
      }
      controls.push(control);
      reviewed.push(wantReviewed ? p.getReviewedText("Current") : null);
    }
    const changeParas: Paragraph[] = [];
    if (tracked !== null) {
      for (const change of tracked.items) {
        const first = change.getRange().paragraphs.getFirst();
        first.load("uniqueLocalId");
        changeParas.push(first);
      }
    }
    const commentRanges: Range[] = [];
    const commentParas: Paragraph[] = [];
    if (comments !== null) {
      for (const comment of comments.items) {
        const range = comment.getRange();
        range.load("text");
        commentRanges.push(range);
        const first = range.paragraphs.getFirst();
        first.load(has16 ? "uniqueLocalId,text" : "text");
        commentParas.push(first);
        comment.replies.load("items/authorName,items/content");
      }
    }
    const storyCollections: { story: Story; collection: ParagraphCollection }[] = [];
    const loadNote = (kind: "fn" | "en", note: NoteItem, n: number): void => {
      const story: Story = { kind, note: n + 1 };
      note.body.paragraphs.load(itemsOf(storyProps));
      storyCollections.push({ story, collection: note.body.paragraphs });
      if (has16) {
        const changes = note.body.getTrackedChanges();
        changes.load("items/type,items/author,items/date,items/text");
        noteTracked.push({ story, changes });
      }
    };
    footnotes?.items.forEach((note, n) => loadNote("fn", note, n));
    endnotes?.items.forEach((note, n) => loadNote("en", note, n));
    const hfCollections: { story: Story; collection: ParagraphCollection }[] = [];
    doc.sections.items.forEach((section, s) => {
      for (let slot = 0; slot < HF_SLOTS; slot++) {
        const collection = hfStory(section, slot).paragraphs;
        collection.load(itemsOf(storyProps));
        hfCollections.push({ story: { kind: "hf", k: s * HF_SLOTS + slot }, collection });
      }
    });
    await sync();

    // ---- assemble ---------------------------------------------------------------------------------------------
    const paragraphs: SnapshotParagraph[] = [];
    const pidByUid = new Map<string, string>();
    const pidsByText = new Map<string, string[]>();
    const remember = (pid: string, uid: string | undefined, text: string): void => {
      if (uid) pidByUid.set(uid, pid);
      const list = pidsByText.get(text);
      if (list) list.push(pid);
      else pidsByText.set(text, [pid]);
    };
    let tableIndex = -1;
    let inTable = false;
    for (let i = 0; i < bodyItems.length; i++) {
      const p = bodyItems[i] as Paragraph;
      const text = replaceLoneSurrogates(p.text);
      const list = lists[i];
      const cell = cells[i];
      const control = controls[i];
      const rev = reviewed[i];
      const nowInTable = p.tableNestingLevel > 0;
      if (nowInTable && !inTable) tableIndex++;
      inTable = nowInTable;
      const pid = `p${i}`;
      paragraphs.push({
        pid,
        text_live: text,
        text_reviewed: rev ? stripParagraphMark(replaceLoneSurrogates(rev.value)) : text,
        list_string: list && !list.isNullObject && list.listString ? replaceLoneSurrogates(list.listString) : null,
        style: p.style ? replaceLoneSurrogates(p.style) : null,
        in_table: cell && !cell.isNullObject ? { table: tableIndex, row: cell.rowIndex, cell: cell.cellIndex } : null,
        locked: Boolean(control && !control.isNullObject && control.cannotEdit),
        hash: await paragraphHash(text),
      });
      remember(pid, has16 ? p.uniqueLocalId : undefined, text);
    }
    const addStory = async (story: Story, collection: ParagraphCollection, skipIfBlank: boolean): Promise<void> => {
      const items = collection.items;
      if (skipIfBlank && items.every((p) => !p.text.trim())) return;
      for (let i = 0; i < items.length; i++) {
        const p = items[i] as Paragraph;
        const text = replaceLoneSurrogates(p.text);
        const pid = pidFor(story, i);
        paragraphs.push({
          pid,
          text_live: text,
          text_reviewed: text,
          list_string: null,
          style: p.style ? replaceLoneSurrogates(p.style) : null,
          in_table: null,
          locked: false,
          hash: await paragraphHash(text),
        });
        remember(pid, has16 ? p.uniqueLocalId : undefined, text);
      }
    };
    for (const { story, collection } of storyCollections) await addStory(story, collection, false);
    for (const { story, collection } of hfCollections) await addStory(story, collection, true);

    const pidOf = (paragraph: Paragraph): string | null => {
      if (has16) return pidByUid.get(paragraph.uniqueLocalId) ?? null;
      const matches = pidsByText.get(replaceLoneSurrogates(paragraph.text));
      return matches && matches.length === 1 ? (matches[0] as string) : null;
    };

    // ---- sync 3 (only with note changes or tracked deletions): which paragraph, and where each deletion is ----
    // Word on the web keeps tracked-deleted text in Paragraph.text (spike P2), so a quote could match words a
    // pending change deletes. Each change's text is read from its range (`TrackedChange.text` is shifted by one
    // character in notes, spike P7) and each deletion is placed by `placeDeletion` (verified, never searched).
    // A note's prefixes carry its reference mark (N1): the Content range of every paragraph that can hold a
    // deletion's start is read in the same sync (once per paragraph) to count it (`leadChars`).
    // Guarded: a failure here only costs the note changes and the placement (no `live_segments`).
    interface PendingChange {
      change: TrackedChange;
      first: Paragraph | null;
      whole: Range | null;
      start: Range | null;
      prefix: Range | null;
    }
    const pendingOf = (change: TrackedChange, first: Paragraph | null): PendingChange => ({ change, first, whole: null, start: null, prefix: null });
    const bodyPending: PendingChange[] = (tracked?.items ?? []).map((change, j) => pendingOf(change, changeParas[j] as Paragraph));
    const notePending: PendingChange[] = noteTracked.flatMap(({ changes }) => changes.items.map((change) => pendingOf(change, null)));
    let placedOk = false;
    const contentByPid = new Map<string, Range>();
    if (notePending.length > 0 || bodyPending.some((p) => p.change.type === "Deleted")) {
      try {
        const bodyIndexByUid = new Map<string, number>();
        if (has16) bodyItems.forEach((p, i) => bodyIndexByUid.set(p.uniqueLocalId, i));
        const wantContent = (pid: string, paragraph: Paragraph | undefined): void => {
          if (paragraph === undefined || contentByPid.has(pid)) return;
          const content = paragraph.getRange("Content");
          content.load("text");
          contentByPid.set(pid, content);
        };
        bodyPending.forEach((pending) => {
          if (pending.change.type !== "Deleted" || pending.first === null) return;
          const i = bodyIndexByUid.get(pending.first.uniqueLocalId);
          if (i !== undefined) wantContent(`p${i}`, bodyItems[i]);
        });
        for (const { story, changes } of noteTracked) {
          if (!changes.items.some((c) => c.type === "Deleted")) continue;
          const collection = storyCollections.find((s) => s.story === story)?.collection;
          collection?.items.forEach((p, i) => wantContent(pidFor(story, i), p));
        }
        for (const pending of [...bodyPending, ...notePending]) {
          const whole = pending.change.getRange();
          whole.load("text");
          pending.whole = whole;
          if (pending.first === null) {
            const first = whole.paragraphs.getFirst();
            first.load("uniqueLocalId");
            pending.first = first;
          }
          if (pending.change.type !== "Deleted") continue;
          const start = pending.change.getRange("Start");
          start.load("text");
          pending.start = start;
          const prefix = pending.first.getRange("Start").expandTo(start);
          prefix.load("text");
          pending.prefix = prefix;
        }
        await sync();
        placedOk = true;
      } catch {
        placedOk = false;
      }
    }
    const changeText = (pending: PendingChange): string =>
      replaceLoneSurrogates(placedOk && pending.whole !== null ? pending.whole.text : (pending.change.text ?? ""));

    const trackedChanges: SnapshotTrackedChange[] = [];
    let droppedTrackedChanges = 0;
    const seenChanges = new Set<string>();
    const pendingPids: (string | null)[] = [];
    const addChange = (pending: PendingChange, fromNote: boolean): void => {
      const change = pending.change;
      const type = trackedType(change.type);
      const pid = pending.first !== null && (!fromNote || placedOk) ? pidOf(pending.first) : null;
      pendingPids.push(pid);
      if (type === null) return;
      if (pid === null) {
        droppedTrackedChanges++;
        return;
      }
      const text = changeText(pending);
      const author = replaceLoneSurrogates(change.author ?? "");
      const key = `${pid}\u0000${type}\u0000${author}\u0000${text}`;
      if (fromNote && seenChanges.has(key)) return; // a host whose body list already had it
      seenChanges.add(key);
      trackedChanges.push({ seq: trackedChanges.length, pid, type, author, date: isoDate(change.date), text });
    };
    for (const pending of bodyPending) addChange(pending, false);
    for (const pending of notePending) addChange(pending, true);

    // Deleted text inside text_live -> live_segments; a paragraph whose deletions do not all place exactly (or,
    // in the body, whose placement disagrees with Word's own accepted view) gets none: its mask is unknown, and
    // the pane refuses edits there at apply time.
    const byPid = new Map(paragraphs.map((p) => [p.pid, p]));
    const readsByStory = new Map<string, { story: Story; reads: (DeletionRead | { first: number; pieces: number })[] }>();
    [...bodyPending, ...notePending].forEach((pending, j) => {
      const pid = pendingPids[j];
      if (pending.change.type !== "Deleted" || pid === null || pid === undefined) return;
      const ref = parsePid(pid);
      if (ref === null) return;
      const key = storyKey(pid);
      let entry = readsByStory.get(key);
      if (entry === undefined) {
        entry = { story: ref.story, reads: [] };
        readsByStory.set(key, entry);
      }
      const text = changeText(pending);
      const content = contentByPid.get(pidFor(ref.story, ref.index));
      const para = byPid.get(pidFor(ref.story, ref.index));
      const lead = placedOk && content !== undefined && para !== undefined ? leadChars(replaceLoneSurrogates(content.text), para.text_live) : null;
      entry.reads.push(
        lead !== null && pending.prefix !== null && pending.start !== null
          ? { first: ref.index, text, start: replaceLoneSurrogates(pending.start.text), prefix: replaceLoneSurrogates(pending.prefix.text), lead }
          : { first: ref.index, pieces: deletionPieces(text).length },
      );
    });
    let deletedTextPlaced = 0;
    let deletedTextUnplaced = 0;
    for (const { story, reads } of readsByStory.values()) {
      const textAt = (index: number): string | undefined => byPid.get(pidFor(story, index))?.text_live;
      const { spans, unknown } = placeDeletions(reads, textAt);
      for (const index of unknown) if (byPid.has(pidFor(story, index))) deletedTextUnplaced++;
      for (const [index, list] of spans) {
        const para = byPid.get(pidFor(story, index));
        const segments = para === undefined ? null : segmentsFromSpans(para.text_live, list);
        if (para === undefined || segments === null) continue;
        if (story.kind === "body") {
          // Word's reviewed text stays text_reviewed; it must agree with the placement.
          if (wantReviewed && !reviewedAgrees(para.text_live, list, para.text_reviewed)) {
            deletedTextUnplaced++;
            continue;
          }
        } else {
          // Notes: Word's reviewed text is wrong there (N2: shifted by one character on the web; P2 B2 saw it keep
          // deleted words), so it is never read; the verified placement gives the accepted text.
          para.text_reviewed = withoutDeleted(segments);
        }
        para.live_segments = segments;
        deletedTextPlaced++;
      }
    }

    const snapshotComments: SnapshotComment[] = [];
    let droppedComments = 0;
    const seenIds = new Set<string>();
    comments?.items.forEach((comment, j) => {
      const pid = pidOf(commentParas[j] as Paragraph);
      if (pid === null || !comment.id || seenIds.has(comment.id)) {
        droppedComments++;
        return;
      }
      seenIds.add(comment.id);
      snapshotComments.push({
        id: comment.id,
        pid,
        quote: replaceLoneSurrogates((commentRanges[j] as Range).text),
        author: replaceLoneSurrogates(comment.authorName ?? ""),
        text: replaceLoneSurrogates(comment.content ?? ""),
        resolved: Boolean(comment.resolved),
        replies: comment.replies.items.map((r) => ({
          author: replaceLoneSurrogates(r.authorName ?? ""),
          text: replaceLoneSurrogates(r.content ?? ""),
        })),
      });
    });

    const pidStart = pidOf(selFirst);
    const pidEnd = pidOf(selLast);
    const selectionOut =
      !selection.isEmpty && pidStart !== null && pidEnd !== null
        ? { pid_start: pidStart, pid_end: pidEnd, text: replaceLoneSurrogates(selection.text) }
        : null;

    let charCount = 0;
    for (const p of paragraphs) charCount += codePointLength(p.text_live);
    if (charCount > MAX_SNAPSHOT_CHARS) throw new SnapshotTooLargeError(charCount);

    // ---- last sync (only with a selection/cursor): where it starts inside its paragraph -------------------------
    // Guarded: a failure here costs only the offset (the target then re-finds the selected text by quote).
    let target: SelectionTarget | null = null;
    if (pidStart !== null && pidEnd !== null) {
      let place: { prefix: string; start: string; lead: number | null } | null = null;
      try {
        const start = selection.getRange("Start");
        start.load("text");
        const prefix = selFirst.getRange("Start").expandTo(start);
        prefix.load("text");
        const content = selFirst.getRange("Content");
        content.load("text");
        await sync();
        place = {
          prefix: replaceLoneSurrogates(prefix.text),
          start: replaceLoneSurrogates(start.text),
          lead: leadChars(replaceLoneSurrogates(content.text), replaceLoneSurrogates(selFirst.text)),
        };
      } catch {
        place = null;
      }
      target = selectionTarget(paragraphs, pidStart, pidEnd, selection.isEmpty ? "" : replaceLoneSurrogates(selection.text), place);
    }

    const snapshot: WordSnapshot = {
      schema_version: 1,
      snapshot_id: captureOptions.snapshotId ?? newId(),
      title: replaceLoneSurrogates(doc.properties.title ?? ""),
      paragraphs,
      tracked_changes: trackedChanges,
      comments: snapshotComments,
      selection: selectionOut,
      cursor_pid: pidStart,
      doc_hash: await documentHash(paragraphs),
      char_count: charCount,
      capture_ms: Math.max(0, Math.round(now() - started)),
    };
    return {
      snapshot,
      target,
      diagnostics: {
        droppedTrackedChanges,
        droppedComments,
        footnotes: has15,
        trackedChanges: has16,
        reviewedText: wantReviewed,
        deletedTextPlaced,
        deletedTextUnplaced,
        syncs,
      },
    };
  }

  // ---- apply ------------------------------------------------------------------------------------------------

  interface PlannedHunk {
    range: Range;
    kind: "replace" | "insert";
    location: "Replace" | "Before" | "After";
    text: string;
    /** Where the new text goes, as a UTF-16 offset of the paragraph text the edit was planned on. */
    at: number;
    /** A replace hunk's old text (at `at`); "" for an insertion. */
    old: string;
  }

  type Plan = { kind: "noop" } | { kind: "diff"; hunks: PlannedHunk[] } | { kind: "whole"; insertionOnly: boolean };

  /**
   * Search every hunk's anchor text inside the anchored range. A pure insertion attaches to the nearest real
   * token (never to a space, which Word's search cannot pin down), so it lands as one inserted run and the
   * clause around it keeps the document's own characters (spec §5.2 [1.1]). `whole` = replace the span.
   */
  async function planHunks(ctx: RequestContext, range: Range, matched: string, replacement: string, base: number): Promise<Plan> {
    const diff = wordDiff(matched, replacement);
    if (diff === null) return { kind: "whole", insertionOnly: false };
    if (diff.hunks.length === 0) return { kind: "noop" };
    const insertionOnly = diff.hunks.every((h) => h.oldFrom === h.oldTo);
    const whole: Plan = { kind: "whole", insertionOnly };
    const searches: { needle: string; position: number; kind: PlannedHunk["kind"]; location: PlannedHunk["location"]; text: string }[] = [];
    for (const hunk of diff.hunks) {
      if (hunk.oldFrom < hunk.oldTo) {
        searches.push({ needle: hunk.oldText, position: hunk.oldFrom, kind: "replace", location: "Replace", text: hunk.newText });
        continue;
      }
      const anchor = insertionAnchor(matched, hunk.oldFrom, hunk.newText);
      if (anchor === null) return whole;
      searches.push({ needle: anchor.token.text, position: anchor.token.start, kind: "insert", location: anchor.location, text: anchor.text });
    }
    const pending = searches.map((s) => {
      const escaped = escapeWordSearch(s.needle);
      if (escaped.length > 255 || !s.needle.trim() || s.needle.includes(UNSEARCHABLE)) return null;
      const k = occurrenceIndex(matched, s.needle, s.position);
      if (k < 0) return null;
      const results = range.search(escaped, SEARCH);
      results.load("items/text");
      return { ...s, k, count: occurrenceCount(matched, s.needle), results };
    });
    if (pending.some((p) => p === null)) return whole;
    await ctx.sync();
    const planned: PlannedHunk[] = [];
    for (const p of pending) {
      if (p === null || p.results.items.length !== p.count) return whole;
      const hit = p.results.items[p.k];
      // Word's search can be looser than ours (quotes, case): the hit must be the very characters we planned on.
      if (hit === undefined || normalizeForMatch(replaceLoneSurrogates(hit.text)).text !== normalizeForMatch(p.needle).text) return whole;
      const at = base + p.position + (p.location === "After" ? p.needle.length : 0);
      planned.push({ range: hit, kind: p.kind, location: p.location, text: forFile(p.text), at, old: p.kind === "replace" ? p.needle : "" });
    }
    return { kind: "diff", hunks: planned };
  }

  /**
   * A delete-only edit's span grown by one space (`smartDeleteSpan`), as a Word range. Needs the paragraph's
   * placed deletions (`state`; a host without WordApi 1.6 only when it is not the web, whose `Paragraph.text`
   * then holds no deleted text). The grown range must read back exactly and hold no pending change. Null: keep
   * the anchored span.
   */
  async function smartDeleteRange(ctx: RequestContext, located: Located, state: ParagraphState | null): Promise<{ range: Range; start: number; end: number } | null> {
    const deleted = state !== null ? state.deleted : webHost ? null : [];
    if (deleted === null) return null;
    const [start, end] = smartDeleteSpan(located.text, located.start, located.end, deleted);
    if (start === located.start && end === located.end) return null;
    try {
      const range = await rangeForSpan(ctx, located.paragraph, located.text, start, end);
      if (range === null) return null;
      if (supports("1.6")) {
        const changes = range.getTrackedChanges();
        changes.load("items/type");
        await ctx.sync();
        if (changes.items.some((c) => c.type === "Added" || c.type === "Deleted")) return null;
      }
      return { range, start, end };
    } catch {
      return null;
    }
  }

  /** The text a retried comment re-anchors on: the edited words, else the paragraph they are in. */
  function retryQuote(edited: string | null, paragraph: string | null, edit: PaneEdit): string {
    const line = (edited ?? "").split(/[\r\n\u2029]+/).find((part) => part.trim());
    if (line !== undefined) return line;
    if (paragraph !== null && paragraph.trim()) return paragraph;
    return edit.op === "delete" ? "" : (edit.replacement.split(/[\r\n]+/).find((part) => part.trim()) ?? "");
  }

  async function applyEdit(ctx: RequestContext, edit: PaneEdit, applyOptions: ApplyOptions): Promise<ApplyResult> {
    const anchored = await anchorRange(ctx, { pid: edit.pid, quote: edit.quote, paragraphHash: edit.paragraphHash }, true);
    if (!anchored.ok) return { status: anchored.failure.status === "ok" ? "error" : anchored.failure.status, detail: anchored.failure.detail };
    const { located, mode, protection, state } = anchored.anchored;
    let range = anchored.anchored.range;
    if (protectionBlocks(protection, "edit")) return { status: "protected", detail: PROTECTED_DETAIL };
    if (applyOptions.mode === "clean" && mode !== "Off") {
      return { status: "refused", detail: "Track Changes is on in this document, so Ella only applies edits as tracked changes." };
    }
    // The span actually changed: the anchored quote, or (delete-only) that quote plus one space (smart cut).
    let spanStart = located.start;
    let matched = located.text.slice(located.start, located.end);
    if (edit.op === "delete") {
      const smart = await smartDeleteRange(ctx, located, state);
      if (smart !== null) {
        range = smart.range;
        spanStart = smart.start;
        matched = located.text.slice(smart.start, smart.end);
      }
    }

    let hunks: PlannedHunk[] = [];
    let imprecise = false;
    let method: NonNullable<ApplyResult["method"]>;
    if (edit.op === "delete") method = "delete";
    else if (edit.op === "insert_after") method = "insert";
    else {
      const plan = await planHunks(ctx, range, matched, edit.replacement, spanStart);
      if (plan.kind === "noop") return { status: "noop", method: "diff", changes: 0, detail: "The document already says this (only typography differs)." };
      if (plan.kind === "diff") {
        method = "diff";
        hunks = plan.hunks;
      } else {
        method = "whole";
        imprecise = plan.insertionOnly;
      }
    }

    // What Ella inserts, in document order (hunks are planned left to right): the stray-space check reads it.
    const ellaTexts = method === "diff" ? hunks.map((h) => h.text) : method === "delete" ? [] : [forFile(edit.replacement)];
    const insertedText = ellaTexts.join("");
    const before = state?.before ?? null;

    // In a footnote/endnote a replace is written as "insert the new text Before the old text's range, then delete
    // that range", in one sync (spike P9 N4, B1: 5/5 clean, change list [i:new][d:old]). Word on the web's own
    // `insertText(new, 'Replace')` there added a stray space 7/7 (a separate no-break-space insertion, or one merged
    // into Ella's insertion). Body and header/footer replaces keep 'Replace' (clean in the body, Attempt 8), and so
    // does every other host: Before + delete is only proven on the web (a host whose 'Before' grows the range
    // would delete Ella's new text with it).
    const inNote = webHost && /^(?:fn|en)/.test(located.pid);
    const replaceIn = (target: Range, text: string): Range => {
      if (!inNote) return target.insertText(text, "Replace");
      if (!text) {
        target.delete();
        return target;
      }
      const inserted = target.insertText(text, "Before");
      target.delete();
      return inserted;
    };

    // Step 1: the edit, under the tracking mode asked for, in its own sync; then (same mode) any space Word
    // itself added next to it is taken out again (spike P2 B1).
    const outcome = await withTracking(
      ctx,
      mode,
      applyOptions.mode,
      async () => {
        let commentTarget: Range = range;
        const insertedRanges: Range[] = [];
        let changes = 1;
        if (method === "delete") range.delete();
        else if (method === "insert") commentTarget = range.insertText(forFile(edit.replacement), "After");
        else if (method === "whole") insertedRanges.push(replaceIn(range, forFile(edit.replacement)));
        else {
          changes = hunks.length;
          // Last to first: earlier hunk ranges are unaffected by later edits.
          for (let i = hunks.length - 1; i >= 0; i--) {
            const h = hunks[i] as PlannedHunk;
            if (h.kind === "insert") insertedRanges.push(h.range.insertText(h.text, h.location));
            else if (h.text) insertedRanges.push(replaceIn(h.range, h.text));
            else h.range.delete();
          }
        }
        await ctx.sync();
        let spacing: { removed: number; verified: boolean; readable?: false } = { removed: 0, verified: true };
        if (method !== "delete") {
          try {
            spacing = await cleanStraySpaces(ctx, located, before, insertedText, ellaTexts);
          } catch {
            spacing = { removed: 0, verified: false };
          }
        }
        // Note Before + delete: P9 proved it on search hits only; check that Word placed exactly what was planned.
        let placementOk = true;
        if (inNote && (method === "whole" || hunks.some((h) => h.kind === "replace" && h.text))) {
          const events =
            method === "whole"
              ? [{ at: spanStart, text: forFile(edit.replacement), old: matched, after: false }]
              : hunks.map((h) => ({ at: h.at, text: h.text, old: h.old, after: h.location === "After" }));
          try {
            placementOk = await noteWriteLandedAsPlanned(ctx, located, before, events);
          } catch {
            placementOk = false;
          }
        }
        return { changes, commentTarget, insertedRanges, spacing, placementOk };
      },
      applyOptions.journal,
    );
    if (outcome.refused) {
      return { status: "refused", detail: "Track Changes is on in this document, so Ella only applies edits as tracked changes." };
    }
    const spacingOk = outcome.value.spacing.verified;
    const placementOk = outcome.value.placementOk;
    // A wrong note placement is the honest reason when the spacing looked fine, or the text could not be read as
    // old + Ella's text at all; a stray that is still there (text readable) is a spacing problem.
    const placementDetail = !placementOk && (spacingOk || outcome.value.spacing.readable === false);
    const result: ApplyResult = {
      status: spacingOk && placementOk ? "applied" : "applied_unverified",
      method,
      changes: outcome.value.changes,
      detail: placementDetail ? PLACEMENT_UNVERIFIED_DETAIL : spacingOk && placementOk ? "" : SPACING_UNVERIFIED_DETAIL,
      modeRestoreFailed: outcome.modeRestoreFailed,
      ...(imprecise ? { imprecise: true } : {}),
      ...(outcome.value.spacing.removed > 0 ? { spacesRemoved: outcome.value.spacing.removed } : {}),
      ...(spacingOk ? {} : { spacingRepairFailed: true }),
      ...(placementOk ? {} : { placementUnverified: true }),
    };
    let commentTarget = outcome.value.commentTarget;

    // Step 2: re-read the story, so later cards re-anchor on what Word now holds (an edit may add paragraphs).
    let editedText: string | null = null;
    let paragraphText: string | null = null;
    try {
      located.collection.load("items/text");
      commentTarget.load("text");
      await ctx.sync();
      const texts = located.collection.items.map((p) => replaceLoneSurrogates(p.text));
      const after = texts[located.index];
      if (after !== undefined) {
        paragraphText = after;
        result.pid = located.pid;
        result.paragraphHashAfter = await paragraphHash(after);
        result.paragraphDelta = texts.length - located.storyLength;
      }
      editedText = replaceLoneSurrogates(commentTarget.text);
      if (method === "delete" && applyOptions.mode === "tracked" && result.paragraphDelta === 0) {
        // F5: the deleted range is now empty on the web; comment on the new Deleted change instead, else (Word
        // kept the words in the range, as on a host without that behaviour) on the range, else the paragraph.
        const paragraph = located.collection.items[located.index] as Paragraph;
        const deletion = supports("1.6") && after !== undefined ? await newDeletionRange(ctx, paragraph, after, spanStart, matched) : null;
        if (deletion !== null) {
          commentTarget = deletion;
          editedText = matched;
        } else if (withoutCommentMarks(editedText) !== withoutCommentMarks(matched)) {
          commentTarget = paragraph.getRange("Content");
          editedText = null;
          result.detail = COMMENT_ON_PARAGRAPH_DETAIL;
        }
      }
      if ((method === "diff" || method === "whole") && after !== undefined && result.paragraphDelta === 0) {
        // Word on the web keeps a Range at its old offsets and length across a tracked edit, so the anchored
        // range no longer covers the edited text and the comment ended mid-word (spike P2 B5). Find it again.
        const fresh = await editedSpanRange(ctx, located, after, outcome.value.insertedRanges);
        if (fresh !== null) {
          commentTarget = fresh.range;
          editedText = fresh.text;
        }
      }
    } catch {
      // The edit is in; later cards still re-anchor by paragraph hash and quote.
    }

    // Step 3: the comment, separately. If it fails the edit stays applied and only the comment is retried.
    if (applyOptions.mode === "tracked") {
      const reason = applyOptions.includeReason && edit.why.trim() ? `\n\n${edit.why.trim()}` : "";
      let text = `${ELLA_COMMENT}${reason}`;
      try {
        const note = await noteReference(ctx, result.pid ?? located.pid);
        if (note !== null) {
          commentTarget = note.range;
          text = `${ELLA_COMMENT} (${note.label})${reason}`;
          result.commentOnNote = note.label;
        }
        const comment = commentTarget.insertComment(text);
        comment.load("id");
        await ctx.sync();
        result.commentId = comment.id;
        // Word on the web puts the comment's anchor mark (U+0005) into Paragraph.text (the edited paragraph's, or
        // the body paragraph holding a note's reference): paragraphHash ignores it, so no hash needs refreshing (B6).
      } catch (error) {
        delete result.commentOnNote;
        result.commentError = isProtectionError(error) ? PROTECTED_DETAIL : errorMessage(error);
        const quote = retryQuote(editedText, paragraphText, edit);
        if (quote) {
          result.retryComment = {
            pid: result.pid ?? located.pid,
            ...(result.paragraphHashAfter ? { paragraphHash: result.paragraphHashAfter } : {}),
            quote,
            text,
          };
        }
      }
    }
    return result;
  }

  /**
   * Take out a space Word itself added next to Ella's edit (spike P7 F4 / P9 N4: in a footnote on the web a
   * tracked replace also inserts a space). The paragraph text must first show the extra spaces (`strayWhitespace`,
   * a non-null reading). Two shapes are repaired:
   * - separate: the strays are identified from Word's own change list (`newStraySpaces`) and rejected only when the
   *   text shows at least as many extra spaces as there are such changes (never a co-author's concurrent space);
   * - merged (N5): no such change, but Ella's NEW `Added` change — identified by its verified position inside the
   *   edited span, never by text alone — reads as one of Ella's planned insertions plus exactly the extra spaces at
   *   its edge (`trimMergedStrays`); those characters are deleted inside it (her own insertion, so they go).
   * Then the paragraph is re-read. `verified` is false whenever a stray may remain, including every case where, on
   * the web, the text cannot be read as old text + Ella's text (before or after the repair), and a note edit that
   * split the paragraph. Syncs: 1 when clean; separate: 5 (read, change ranges, reject, re-read, change ranges);
   * merged: 8 (read, change ranges, change positions, word search, edge ranges, delete, re-read, change ranges).
   * Only this paragraph is read.
   */
  async function cleanStraySpaces(
    ctx: RequestContext,
    located: Located,
    before: readonly ChangeSig[] | null,
    inserted: string,
    ellaTexts: readonly string[],
  ): Promise<{ removed: number; verified: boolean; readable?: false }> {
    const inNote = /^(?:fn|en)/.test(located.pid);
    // A paragraph break in Ella's text split the paragraph: no in-place reading (a note must not split).
    if (/[\r\n]/.test(inserted)) return { removed: 0, verified: !inNote };
    const paragraph = located.paragraph;
    const read = async (): Promise<{ text: string; strays: number[] | null; changes: TrackedChangeCollection | null }> => {
      paragraph.load("text");
      const changes = before !== null ? paragraph.getTrackedChanges() : null;
      changes?.load("items/type,items/author");
      await ctx.sync();
      const text = replaceLoneSurrogates(paragraph.text);
      return { text, strays: strayWhitespace(located.text, located.start, located.end, text, inserted), changes };
    };
    const first = await read();
    // On the web the text always reads as old + Ella's text (deleted words stay): anything else is unverified.
    // Other hosts drop deleted words from Paragraph.text, so there is no reading to check (and no F4 stray seen).
    if (first.strays === null) return webHost ? { removed: 0, verified: false, readable: false } : { removed: 0, verified: true };
    if (first.strays.length === 0) return { removed: 0, verified: true };
    if (first.changes === null || before === null) return { removed: 0, verified: false };
    const strays = await newStraySpaces(ctx, first.changes, before, ellaTexts);
    let removed: number;
    if (strays !== null && strays.length === 0) {
      const trimmed = await trimMergedStrays(ctx, located, first.text, first.changes, first.strays, ellaTexts);
      if (trimmed === null) return { removed: 0, verified: false };
      removed = trimmed;
    } else {
      if (strays === null || strays.length > first.strays.length) return { removed: 0, verified: false };
      for (const change of strays) change.reject();
      await ctx.sync();
      removed = strays.length;
    }
    const now = await read();
    const left = now.changes === null ? null : await newStraySpaces(ctx, now.changes, before, ellaTexts);
    return {
      removed,
      verified: now.strays !== null && now.strays.length === 0 && left !== null && left.length === 0,
      ...(now.strays === null ? { readable: false as const } : {}),
    };
  }

  /**
   * N5 (spike P9 A1): the stray merged into Ella's own insertion ("Contract␣" before the deleted "Agreement").
   * `after` is the paragraph text now, `strayAt` the extra spaces `strayWhitespace` found there. Each `Added`
   * change is placed like a deletion (`placeDeletion`: prefix − lead − start, verified against `after`); only a
   * change lying inside the edited span (which held no pending change before the edit) and covering a stray is
   * Ella's new one — an older identical change elsewhere never is. It must read as exactly one of `ellaTexts` plus
   * whitespace at its edges at exactly those stray offsets, and every stray must be explained so. Each edge is
   * found as the range between the planned word (searched once, inside the change) and the change's edge, must
   * read back as that whitespace, and is deleted. Returns how many characters were deleted; null = any doubt
   * (nothing deleted).
   */
  async function trimMergedStrays(
    ctx: RequestContext,
    located: Located,
    after: string,
    changes: TrackedChangeCollection,
    strayAt: readonly number[],
    ellaTexts: readonly string[],
  ): Promise<number | null> {
    const paragraph = located.paragraph;
    const added = changes.items.filter((c) => c.type === "Added");
    if (added.length === 0) return null;
    const content = paragraph.getRange("Content");
    content.load("text");
    const reads = added.map((change) => {
      const whole = change.getRange();
      whole.load("text");
      const start = change.getRange("Start");
      start.load("text");
      const prefix = paragraph.getRange("Start").expandTo(start);
      prefix.load("text");
      return { whole, start, prefix };
    });
    await ctx.sync();
    const lead = leadChars(replaceLoneSurrogates(content.text), after);
    if (lead === null) return null;
    const spanEnd = after.length - (located.text.length - located.end);
    const planned = ellaTexts.map(sameSpaces);
    const fixes: { whole: Range; core: string; head: string; tail: string }[] = [];
    let explained = 0;
    for (const r of reads) {
      const text = replaceLoneSurrogates(r.whole.text);
      const placed = placeDeletion(
        { first: 0, text, start: replaceLoneSurrogates(r.start.text), prefix: replaceLoneSurrogates(r.prefix.text), lead },
        (i) => (i === 0 ? after : undefined),
      );
      const span = placed !== null && placed.length === 1 ? placed[0]!.span : null;
      if (span === null) {
        // An Added change Word placed nowhere we can verify: if it may hold a stray, stop.
        if (isStraySpaceText(text.slice(0, 1)) || isStraySpaceText(text.slice(-1))) return null;
        continue;
      }
      const [a, b] = span;
      const inside = strayAt.filter((x) => a <= x && x < b);
      if (inside.length === 0) continue;
      if (a < located.start || b > spanEnd) return null; // not Ella's new change
      const same = sameSpaces(text);
      const fits: { x: number; y: number }[] = [];
      for (let x = 0; x <= inside.length; x++) {
        const y = inside.length - x;
        const core = same.slice(x, same.length - y);
        if (!core.trim() || !planned.includes(core)) continue;
        if (x > 0 && !isStraySpaceText(same.slice(0, x))) continue;
        if (y > 0 && !isStraySpaceText(same.slice(same.length - y))) continue;
        const at = [...Array(x).keys()].map((i) => a + i).concat([...Array(y).keys()].map((j) => b - y + j));
        if (at.every((p, i) => p === inside[i])) fits.push({ x, y });
      }
      if (fits.length !== 1) return null;
      const { x, y } = fits[0]!;
      fixes.push({ whole: r.whole, core: text.slice(x, text.length - y), head: text.slice(0, x), tail: text.slice(text.length - y) });
      explained += inside.length;
    }
    if (fixes.length === 0 || explained !== strayAt.length) return null;
    const searches = fixes.map((f) => {
      const escaped = escapeWordSearch(f.core);
      if (escaped.length > 255 || f.core.includes(UNSEARCHABLE)) return null;
      const hits = f.whole.search(escaped, SEARCH);
      hits.load("items/text");
      return hits;
    });
    if (searches.some((h) => h === null)) return null;
    await ctx.sync();
    const edges: { range: Range; want: string }[] = [];
    for (const [i, f] of fixes.entries()) {
      const hits = searches[i]!.items;
      const word = hits[0];
      if (hits.length !== 1 || word === undefined || sameSpaces(replaceLoneSurrogates(word.text)) !== sameSpaces(f.core)) return null;
      if (f.tail) edges.push({ range: word.getRange("End").expandTo(f.whole.getRange("End")), want: f.tail });
      if (f.head) edges.push({ range: f.whole.getRange("Start").expandTo(word.getRange("Start")), want: f.head });
    }
    for (const e of edges) e.range.load("text");
    await ctx.sync();
    if (edges.some((e) => sameSpaces(replaceLoneSurrogates(e.range.text)) !== sameSpaces(e.want))) return null;
    for (const e of edges) e.range.delete();
    await ctx.sync();
    return edges.reduce((n, e) => n + e.want.length, 0);
  }

  /**
   * After a note replace written as Before + delete (web): did Word put exactly the planned changes where planned?
   * `events` are the edit's insertions in the planned paragraph text's offsets (`at`; `after`: attached After a
   * token, so it precedes a Before insertion at the same point); each with `old` text is a replace. Word on the web
   * keeps deleted words in `Paragraph.text`, so a replace must now read `[Added new][Deleted old]` starting at `at`
   * plus the length of every insertion before it. Every change of the paragraph is placed by the N1 rule
   * (`placeDeletion`); each planned change must be there at exactly its span with exactly its text, be new since
   * `before` (by type, author, text), and no other change may overlap it; a change that cannot be placed while it
   * is new also fails. Two syncs. False = any doubt.
   */
  async function noteWriteLandedAsPlanned(
    ctx: RequestContext,
    located: Located,
    before: readonly ChangeSig[] | null,
    events: readonly { at: number; text: string; old: string; after: boolean }[],
  ): Promise<boolean> {
    if (before === null || !supports("1.6")) return false;
    const paragraph = located.paragraph;
    paragraph.load("text");
    const changes = paragraph.getTrackedChanges();
    changes.load("items/type,items/author");
    await ctx.sync();
    const after = replaceLoneSurrogates(paragraph.text);
    const content = paragraph.getRange("Content");
    content.load("text");
    const reads = changes.items.map((change) => {
      const whole = change.getRange();
      whole.load("text");
      const start = change.getRange("Start");
      start.load("text");
      const prefix = paragraph.getRange("Start").expandTo(start);
      prefix.load("text");
      return { change, whole, start, prefix };
    });
    await ctx.sync();
    const lead = leadChars(replaceLoneSurrogates(content.text), after);
    if (lead === null) return false;
    const placed = reads.map((r) => {
      const sig: ChangeSig = { type: r.change.type, author: replaceLoneSurrogates(r.change.author ?? ""), text: replaceLoneSurrogates(r.whole.text) };
      const at = placeDeletion(
        { first: 0, text: sig.text, start: replaceLoneSurrogates(r.start.text), prefix: replaceLoneSurrogates(r.prefix.text), lead },
        (i) => (i === 0 ? after : undefined),
      );
      return { sig, span: at !== null && at.length === 1 ? at[0]!.span : null };
    });
    // New since before: per signature, how many more there are now.
    const fresh = new Map<string, number>();
    for (const p of placed) fresh.set(sigKey(p.sig), (fresh.get(sigKey(p.sig)) ?? 0) + 1);
    for (const sig of before) fresh.set(sigKey(sig), (fresh.get(sigKey(sig)) ?? 0) - 1);
    if (placed.some((p) => p.span === null && (fresh.get(sigKey(p.sig)) ?? 0) > 0)) return false;
    const expected: { type: string; text: string; span: [number, number] }[] = [];
    for (const e of events) {
      if (!e.old || !e.text) continue;
      const shift = events.reduce((n, o) => (o !== e && (o.at < e.at || (o.at === e.at && o.after)) ? n + o.text.length : n), 0);
      const a = e.at + shift;
      expected.push({ type: "Added", text: e.text, span: [a, a + e.text.length] });
      expected.push({ type: "Deleted", text: e.old, span: [a + e.text.length, a + e.text.length + e.old.length] });
    }
    for (const x of expected) {
      const hit = placed.filter((p) => p.span !== null && p.sig.type === x.type && p.span[0] === x.span[0] && p.span[1] === x.span[1]);
      if (hit.length !== 1 || sameSpaces(hit[0]!.sig.text) !== sameSpaces(x.text)) return false;
      const key = sigKey(hit[0]!.sig);
      const left = fresh.get(key) ?? 0;
      if (left <= 0) return false;
      fresh.set(key, left - 1);
      const overlaps = placed.filter((p) => p.span !== null && p.span[0] < x.span[1] && x.span[0] < p.span[1]);
      if (overlaps.length !== 1) return false;
    }
    return true;
  }

  /**
   * The edited span as Word now holds it: the anchored span's start up to the unchanged text after it (on the
   * web this includes the tracked-deleted words, which Word still shows). Falls back to the one inserted
   * range of a single-hunk edit. Null: keep the range the edit was made on.
   */
  async function editedSpanRange(
    ctx: RequestContext,
    located: Located,
    after: string,
    insertedRanges: readonly Range[],
  ): Promise<{ range: Range; text: string } | null> {
    const paragraph = located.collection.items[located.index];
    const tail = located.text.length - located.end;
    const end = after.length - tail;
    if (
      paragraph !== undefined &&
      end > located.start &&
      after.slice(0, located.start) === located.text.slice(0, located.start) &&
      after.slice(end) === located.text.slice(located.end)
    ) {
      const range = await rangeForSpan(ctx, paragraph, after, located.start, end);
      if (range !== null) return { range, text: after.slice(located.start, end) };
    }
    if (insertedRanges.length === 1) {
      const only = insertedRanges[0] as Range;
      only.load("text");
      await ctx.sync();
      const text = replaceLoneSurrogates(only.text);
      if (text.trim()) return { range: only, text };
    }
    return null;
  }

  /**
   * Word on the web: a comment about footnote/endnote text goes on the note's reference mark in the main text
   * (spike P2 B3: a comment inside a note is dropped from the exported file, leaving a dangling anchor, and
   * the body's comment list cannot see it to remove it). Null: not a note pid, or not on the web.
   */
  async function noteReference(ctx: RequestContext, pid: string): Promise<{ range: Range; label: string } | null> {
    if (!noteCommentsOnReference || !supports("1.5")) return null;
    const ref = parsePid(pid);
    if (ref === null || (ref.story.kind !== "fn" && ref.story.kind !== "en")) return null;
    const notes = ref.story.kind === "fn" ? ctx.document.body.footnotes : ctx.document.body.endnotes;
    notes.load("items");
    await ctx.sync();
    const note = notes.items[ref.story.note - 1];
    if (note === undefined) return null;
    return { range: note.reference, label: `${ref.story.kind === "fn" ? "footnote" : "endnote"} ${ref.story.note}` };
  }

  /** A Word range at `offset` of `paragraph` (text `text`) to insert against, and on which side. */
  async function rangeAtOffset(
    ctx: RequestContext,
    paragraph: Paragraph,
    text: string,
    offset: number,
  ): Promise<{ range: Range; location: "Before" | "After" | "Replace" } | null> {
    const span = boundarySpan(text, offset);
    if (span === null) return !text.trim() && offset === 0 ? { range: paragraph.getRange("Start"), location: "Replace" } : null;
    const range = await rangeForSpan(ctx, paragraph, text, span.start, span.end);
    return range === null ? null : { range, location: span.location };
  }

  /**
   * The paragraph a captured spot names: its pid with the same hash while the story still has as many
   * paragraphs as at capture, else the one paragraph in the story with that hash. Never a bare index.
   */
  async function paragraphForSpot(
    ctx: RequestContext,
    spot: { pid: string; paragraphHash: string },
    expectedCount: number,
  ): Promise<{ collection: ParagraphCollection; texts: string[]; index: number | null; refIndex: number; sameCount: boolean } | null> {
    const ref = parsePid(spot.pid);
    if (ref === null) return null;
    const collection = await storyParagraphs(ctx, ref.story);
    if (collection === null) return null;
    collection.load("items/text");
    await ctx.sync();
    const texts = collection.items.map((p) => replaceLoneSurrogates(p.text));
    const sameCount = texts.length === expectedCount;
    let index: number | null = null;
    const atPid = texts[ref.index];
    if (sameCount && atPid !== undefined && (await paragraphHash(atPid)) === spot.paragraphHash) {
      index = ref.index;
    } else {
      const hashes = await Promise.all(texts.map((t) => paragraphHash(t)));
      const matches = hashes.flatMap((h, i) => (h === spot.paragraphHash ? [i] : []));
      if (matches.length === 1) index = matches[0] as number;
    }
    return { collection, texts, index, refIndex: ref.index, sameCount };
  }

  type InlineSpot = { host: Paragraph; hostText: string; range: Range; location: "Before" | "After" | "Replace" };

  /** Where "Replace selection" writes: the text selected at capture, wherever the selection is now. */
  async function selectionSpot(ctx: RequestContext, target: SelectionTarget): Promise<InlineSpot | { failure: InsertResult }> {
    const moved = { failure: { status: "not_found" as const, detail: SELECTION_MOVED_DETAIL } };
    if (!target.text.trim()) return { failure: { status: "not_found", detail: "Nothing was selected when you asked Ella. Select the text to replace and ask again." } };
    if (target.start.pid !== target.end.pid) {
      // Several paragraphs: only the live selection can hold that range, so it must still be exactly it.
      const selection = ctx.document.getSelection();
      selection.load("text,isEmpty");
      const first = selection.paragraphs.getFirst();
      const last = selection.paragraphs.getLast();
      first.load("text");
      last.load("text");
      await ctx.sync();
      const same =
        !selection.isEmpty &&
        normalizeForMatch(replaceLoneSurrogates(selection.text).replace(/[\r\n]+$/, "")).text === normalizeForMatch(target.text).text &&
        (await paragraphHash(replaceLoneSurrogates(first.text))) === target.start.paragraphHash &&
        (await paragraphHash(replaceLoneSurrogates(last.text))) === target.end.paragraphHash;
      return same ? { host: last, hostText: replaceLoneSurrogates(last.text), range: selection, location: "Replace" } : moved;
    }
    const found = await paragraphForSpot(ctx, target.start, target.storyParagraphs);
    if (found === null) return moved;
    let index: number | null = null;
    let start: number | null = null;
    let end: number | null = null;
    if (found.index !== null && target.start.offset !== null && target.end.offset !== null) {
      // The paragraph is unchanged: the captured offsets are exact.
      index = found.index;
      start = target.start.offset;
      end = target.end.offset;
    } else {
      // Changed (or offsets unknown): the selected text must be found once, by the anchor ladder, in THAT
      // paragraph — the one the hash names, else its pid while no paragraph was added or removed.
      const at = found.index ?? (found.sameCount ? found.refIndex : null);
      const text = at === null ? undefined : found.texts[at];
      if (at === null || text === undefined) return moved;
      const hit = locate(text, target.text);
      if (hit.status !== "ok" || hit.start === null || hit.end === null) return moved;
      index = at;
      start = hit.start;
      end = hit.end;
    }
    const host = found.collection.items[index] as Paragraph;
    const hostText = found.texts[index] as string;
    const range = await rangeForSpan(ctx, host, hostText, start, end);
    return range === null ? moved : { host, hostText, range, location: "Replace" };
  }

  /** Where "Insert at cursor" writes: the cursor (the end of the selection) as captured. */
  async function cursorSpot(ctx: RequestContext, target: SelectionTarget): Promise<InlineSpot | { failure: InsertResult }> {
    const moved = { failure: { status: "not_found" as const, detail: CURSOR_MOVED_DETAIL } };
    if (target.end.offset === null) return moved;
    const found = await paragraphForSpot(ctx, target.end, target.storyParagraphs);
    if (found === null || found.index === null) return moved;
    const host = found.collection.items[found.index] as Paragraph;
    const hostText = found.texts[found.index] as string;
    const spot = await rangeAtOffset(ctx, host, hostText, target.end.offset);
    return spot === null ? moved : { host, hostText, ...spot };
  }

  async function insertText(ctx: RequestContext, request: InsertRequest): Promise<InsertResult> {
    const blocks = parseMarkdown(request.markdown);
    if (blocks.length === 0) return { status: "not_found", detail: "There is nothing to insert." };
    const doc = ctx.document;

    let hostParagraph: Paragraph;
    let hostText: string | null = null;
    let inline: { range: Range; location: "Before" | "After" | "Replace" } | null = null;
    if (request.at === "after_pid") {
      const ref = request.pid ? parsePid(request.pid) : null;
      if (ref === null) return { status: "not_found", detail: `There is no paragraph ${request.pid ?? ""}.` };
      const collection = await storyParagraphs(ctx, ref.story);
      if (collection === null) return { status: "not_found", detail: `Paragraph ${request.pid ?? ""} is not in this document any more.` };
      collection.load("items/text");
      await ctx.sync();
      const texts = collection.items.map((p) => replaceLoneSurrogates(p.text));
      let index: number | null = ref.index < texts.length ? ref.index : null;
      if (request.paragraphHash !== undefined && (index === null || (await paragraphHash(texts[index] as string)) !== request.paragraphHash)) {
        const hashes = await Promise.all(texts.map((t) => paragraphHash(t)));
        const matches = hashes.flatMap((h, i) => (h === request.paragraphHash ? [i] : []));
        index = matches.length === 1 ? (matches[0] as number) : null;
      }
      if (index === null) return { status: "not_found", detail: "The document changed here; that paragraph was not found." };
      hostParagraph = collection.items[index] as Paragraph;
    } else {
      const target = request.target ?? null;
      if (target === null) {
        return {
          status: "not_found",
          detail:
            request.at === "cursor"
              ? "Ella doesn't know where your cursor was when you asked. Put the cursor where the draft should go and ask again."
              : "Nothing was selected when you asked Ella. Select the text to replace and ask again.",
        };
      }
      const spot = request.at === "cursor" ? await cursorSpot(ctx, target) : await selectionSpot(ctx, target);
      if ("failure" in spot) return spot.failure;
      hostParagraph = spot.host;
      hostText = spot.hostText;
      inline = { range: spot.range, location: spot.location };
    }
    if (supports("1.4")) doc.load("changeTrackingMode");
    if (canReadProtection()) doc.load("protectionType");
    const control = hostParagraph.parentContentControlOrNullObject;
    control.load("cannotEdit");
    if (hostText === null) hostParagraph.load("text");
    await ctx.sync();
    if (!control.isNullObject && control.cannotEdit) return { status: "locked", detail: "That spot is in a locked content control." };
    if (canReadProtection() && protectionBlocks(doc.protectionType ?? null, "edit")) return { status: "protected", detail: PROTECTED_DETAIL };
    const mode = supports("1.4") ? doc.changeTrackingMode : "Off";
    const hostEmpty = !(hostText ?? hostParagraph.text).trim();

    try {
      const outcome = await withTracking(
        ctx,
        mode,
        request.mode,
        async () => {
          let last: Paragraph = hostParagraph;
          let rest = blocks;
          if (inline !== null) {
            // The first block goes where the cursor/selection was; its style only applies on an empty line.
            const first = blocks[0]!;
            let r: Range | null = null;
            for (const run of first.runs) {
              r = r === null ? inline.range.insertText(forFile(run.text), inline.location) : r.insertText(forFile(run.text), "After");
              r.font.bold = run.bold;
              r.font.italic = run.italic;
            }
            const style = builtInStyle(first);
            if (style !== null && hostEmpty) hostParagraph.styleBuiltIn = style;
            rest = blocks.slice(1);
          }
          for (const block of rest) {
            const p = last.insertParagraph("", "After");
            const style = builtInStyle(block);
            p.styleBuiltIn = style ?? "Normal";
            for (const run of block.runs) {
              const r = p.insertText(forFile(run.text), "End");
              r.font.bold = run.bold;
              r.font.italic = run.italic;
            }
            last = p;
          }
          await ctx.sync();
          return blocks.length;
        },
        request.journal,
      );
      if (outcome.refused) return { status: "refused", detail: "Track Changes is on in this document, so Ella only inserts tracked text." };
      return { status: "inserted", paragraphs: outcome.value, detail: "", modeRestoreFailed: outcome.modeRestoreFailed };
    } catch (error) {
      if (isProtectionError(error)) return { status: "protected", detail: PROTECTED_DETAIL, modeRestoreFailed: restoreFailed(error) };
      return { status: "error", detail: errorMessage(error), modeRestoreFailed: restoreFailed(error) };
    }
  }

  // ---- public ------------------------------------------------------------------------------------------------

  const inQueue = <T>(batch: (ctx: RequestContext) => Promise<T>): Promise<T> => queue.run(() => run(batch));

  return {
    capture: (captureOptions = {}) => inQueue((ctx) => capture(ctx, captureOptions)),

    anchor: (target) =>
      inQueue(async (ctx): Promise<AnchorOutcome> => {
        const anchored = await anchorRange(ctx, target, true);
        if (!anchored.ok) {
          return { status: anchored.failure.status, pid: anchored.failure.pid, resolution: null, level: null, matchedText: null, detail: anchored.failure.detail };
        }
        const { located } = anchored.anchored;
        return {
          status: "ok",
          pid: located.pid,
          resolution: located.resolution,
          level: located.level,
          matchedText: located.text.slice(located.start, located.end),
          detail: "",
        };
      }),

    applyEdit: (edit, applyOptions) =>
      inQueue(async (ctx): Promise<ApplyResult> => {
        try {
          return await applyEdit(ctx, edit, applyOptions);
        } catch (error) {
          const status = isProtectionError(error) ? "protected" : "error";
          return { status, detail: status === "protected" ? PROTECTED_DETAIL : errorMessage(error), modeRestoreFailed: restoreFailed(error) };
        }
      }),

    addComment: (comment: PaneComment) =>
      inQueue(async (ctx): Promise<CommentResult> => {
        try {
          const anchored = await anchorRange(ctx, comment, false);
          if (!anchored.ok) {
            const status = anchored.failure.status;
            return { status: status === "pending_change" || status === "ok" ? "error" : status, detail: anchored.failure.detail };
          }
          if (protectionBlocks(anchored.anchored.protection, "comment")) return { status: "protected", detail: PROTECTED_DETAIL };
          let target = anchored.anchored.range;
          let text = comment.text;
          const note = await noteReference(ctx, anchored.anchored.located.pid);
          if (note !== null) {
            target = note.range;
            if (!text.includes(note.label)) text = `${text}\n\n(On ${note.label}: “${clipQuote(comment.quote)}”)`;
          }
          const created = target.insertComment(text);
          created.load("id");
          await ctx.sync();
          return { status: "added", commentId: created.id, detail: "", ...(note !== null ? { commentOnNote: note.label } : {}) };
        } catch (error) {
          if (isProtectionError(error)) return { status: "protected", detail: PROTECTED_DETAIL };
          return { status: "error", detail: errorMessage(error) };
        }
      }),

    insertText: (request) => inQueue((ctx) => insertText(ctx, request)),

    navigate: (pid, quote) =>
      inQueue(async (ctx): Promise<NavigateResult> => {
        try {
          if (quote) {
            const anchored = await anchorRange(ctx, { pid, quote }, false);
            if (!anchored.ok) {
              const status = anchored.failure.status === "ambiguous" ? "ambiguous" : "not_found";
              return { status, detail: anchored.failure.detail };
            }
            anchored.anchored.range.select();
            await ctx.sync();
            return { status: "selected", detail: "" };
          }
          const ref = parsePid(pid);
          const collection = ref ? await storyParagraphs(ctx, ref.story) : null;
          if (ref === null || collection === null) return { status: "not_found", detail: `There is no paragraph ${pid}.` };
          collection.load("items/text");
          await ctx.sync();
          const paragraph = collection.items[ref.index];
          if (paragraph === undefined) return { status: "not_found", detail: `There is no paragraph ${pid}.` };
          paragraph.select();
          await ctx.sync();
          return { status: "selected", detail: "" };
        } catch (error) {
          return { status: "error", detail: errorMessage(error) };
        }
      }),

    getTrackingMode: () =>
      inQueue(async (ctx): Promise<TrackingMode> => {
        if (!supports("1.4")) return "Off";
        ctx.document.load("changeTrackingMode");
        await ctx.sync();
        return asTrackingMode(ctx.document.changeTrackingMode);
      }),

    setTrackingMode: (mode) =>
      inQueue(async (ctx): Promise<void> => {
        if (!supports("1.4")) throw new Error("Track Changes needs Word API 1.4.");
        ctx.document.changeTrackingMode = mode;
        await ctx.sync();
      }),

    removeComments: (ids) =>
      inQueue(async (ctx) => {
        const wanted = new Set(ids);
        const comments = ctx.document.body.getComments();
        comments.load("items/id");
        await ctx.sync();
        const removed: string[] = [];
        for (const comment of comments.items) {
          if (wanted.has(comment.id)) {
            comment.delete();
            removed.push(comment.id);
          }
        }
        await ctx.sync();
        return { removed, missing: ids.filter((id) => !removed.includes(id)) };
      }),
  };
}

/** Hands bound to the real `Word.run` in the current Office host. */
export function officeWordHands(): DocumentHands {
  return createWordHands({
    run: (batch) => Word.run((context) => batch(context)),
    supports: (version) => Office.context.requirements.isSetSupported("WordApi", version),
    supportsDesktop: (version) => Office.context.requirements.isSetSupported("WordApiDesktop", version),
    noteCommentsOnReference: Office.context.platform === Office.PlatformType.OfficeOnline,
    webHost: Office.context.platform === Office.PlatformType.OfficeOnline,
  });
}
